import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import useIsMobile from '../auth/useIsMobile';
import Loading from '../components/Loading';
import Section from '../components/Section';
import { TextField } from '../components/FormField';
import { listPlants } from '../services/plants';
import {
  assignmentsAtPlant,
  coverageCaption,
  coverageEndedOn,
  coverageOn,
  findAssignment,
  listSuppliers,
  serviceLabel,
} from '../services/suppliers';
import { listTeam } from '../services/team';
import { billedTimesheetIds, listInvoices } from '../services/invoices';
import {
  resolveSupplierScope,
  scopeAllowsSupplierAtPlant,
} from '../utils/supplierScope';
import {
  ADJUSTMENT_TYPES,
  BILLING_DISPOSITIONS,
  BILLING_DISPOSITION_LABELS,
  COVERAGE_TYPES,
  COVERAGE_TYPE_LABELS,
  CREDIT_DISPOSITIONS,
  DAY_KEYS,
  EXPLANATION_REQUIRED_COVERAGE_TYPES,
  STATUS,
  SUBMITTER_EDITABLE_STATUSES,
  SUPPLEMENT_DISPOSITIONS,
  BILLING_TRACKER_FIRST_WEEK,
  approveTimesheet,
  computeTotals,
  createAdjustmentTimesheet,
  currentWeekStart,
  dayLabel,
  defaultWeekStart,
  emptyRow,
  getTimesheet,
  getTimesheetById,
  OT_RATE_MULTIPLIER,
  rejectTimesheet,
  rowTotal,
  rowHasData,
  saveDraftTimesheet,
  shiftWeek,
  stripEmptyRows,
  submitTimesheetForApproval,
  updateApprovedTimesheet,
  updatePendingTimesheet,
  uploadTimesheetReceipt,
  uploadTimesheetOverageApproval,
  weekDates,
  weekStartFromDate,
} from '../services/timesheets';
import { calcMileageDollars } from '../config/rates';
import { displayFullName } from '../utils/teamDisplay';

/**
 * Decide whether an Expenses row needs a receipt attached at submit
 * time. A receipt is now required for EVERY expense with an amount,
 * regardless of the dollar amount or the supplier's settings (per
 * Jillian, Sep 2026). Returns false for non-Expenses rows and for a
 * zero/blank amount so the call site doesn't have to keep guarding on
 * coverageType.
 */
// eslint-disable-next-line no-unused-vars
function expenseRowRequiresReceipt(row, supplier) {
  if (!row || row.coverageType !== 'Expenses') return false;
  const amount = Number(row.expenseAmount) || 0;
  return amount > 0;
}

/**
 * Timesheets page — one page per (signed-in user, week). Defaults to the
 * current Monday-anchored week; a small week picker lets the user step
 * back into past weeks. The page shows a status banner that drives
 * editability:
 *
 *   Draft          — submitter can edit
 *   Rejected       — submitter can edit (with RM's reason surfaced)
 *   Pending        — read-only for submitter (waiting on RM)
 *   Approved       — read-only for submitter (RM owns edits now)
 *
 * Hours inputs keep the exact amount entered (no quarter-hour
 * snapping). Mileage rows treat the same day
 * cells as miles and render the IRS-rate dollar total in the Total
 * column. Plant closure days get a subtle gray wash to flag to the
 * submitter that the plant was closed that day.
 */
export default function Timesheets() {
  const { name, email, isDirector, isRegionalManager } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // RM-edit mode — set via ?edit=<timesheetId>. When present the page
  // loads that specific timesheet (rather than "my current week"),
  // shows it to a Director or Regional Manager as fully editable
  // regardless of status, and swaps the footer buttons to
  // "Save & Keep Pending" / "Save & Approve". Used by the Edit button
  // on the Timesheet Approvals page. We capture the id once at mount
  // so navigating in/out of the page cleanly resets state.
  const editingId = searchParams.get('edit') || '';
  const editing = Boolean(editingId);

  // Supplement-of mode — set via ?supplement-of=<parentTimesheetId>.
  // Used when a Director realizes something was missed on an already-
  // approved (or already-invoiced) timesheet. The editor loads the
  // parent for *context only* — submitter identity and week — and
  // starts the user on a fresh empty grid. Saving creates a new
  // timesheet linked to the parent, marked AdjustmentType='supplement',
  // in Pending status so it flows through the normal RM approval queue.
  // Reason + BillingDisposition inputs are required at the bottom.
  const supplementOfId = searchParams.get('supplement-of') || '';
  const supplementing = Boolean(supplementOfId);

  // Credit-of mode — set via ?credit-of=<parentTimesheetId>. Mirror
  // image of supplement mode: used when a Director realizes the
  // customer was over-billed and we owe them a credit. Stored as a
  // flat dollar amount (no row grid) because credits typically need
  // to read as "credit $X for reason Y" on a credit memo, regardless
  // of which row(s) were over-logged. Parent rows are still loaded
  // for read-only reference so the Director can sanity-check what
  // was originally billed.
  const creditOfId = searchParams.get('credit-of') || '';
  const crediting = Boolean(creditOfId);

  // Treat supplement + credit modes as "adjustment mode" wherever the
  // existing code branched on `supplementing`. Saves writing the same
  // OR check at every call site.
  const adjustmentParentId = supplementOfId || creditOfId;
  const adjusting = supplementing || crediting;

  // ---- Page state -------------------------------------------------
  // Lands on the current week, clamped to the Billing Tracker's first
  // open week (Aug 3, 2026) — see defaultWeekStart in services/timesheets.
  const [weekStart, setWeekStart] = useState(defaultWeekStart());
  const [team, setTeam] = useState([]);
  const [plants, setPlants] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [timesheet, setTimesheet] = useState(null); // null = nothing saved yet
  // Retract mode (Nov 2026): a Director editing an already-Approved
  // tracker. Saving overwrites the same record, either straight back to
  // Approved or back to Pending for a fresh approval. invoicedOn holds
  // the invoice number when the tracker is already on a live invoice,
  // so the Director is warned that invoice won't change.
  const [invoicedOn, setInvoicedOn] = useState('');
  const [rows, setRows] = useState([emptyRow()]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [saveNote, setSaveNote] = useState('');
  // Edit-mode only: the original submitter's id, captured from the
  // loaded timesheet so the grid can be scoped to *their* plant list
  // (not the RM's) — matches what the submitter would see if they
  // opened their own timesheet.
  const [editingSubmitterId, setEditingSubmitterId] = useState('');
  // Adjustment-mode (supplement or credit) state — the loaded parent
  // timesheet (for banner + identity scope) and the user-entered
  // Reason + BillingDisposition. Credits additionally collect a
  // CreditAmount (flat dollars). The supplement-* names are kept for
  // backward compatibility with the existing render code; both modes
  // read these same variables.
  const [parentTimesheet, setParentTimesheet] = useState(null);
  const [supplementReason, setSupplementReason] = useState('');
  const [supplementDisposition, setSupplementDisposition] = useState('');
  // Credit-mode only — flat dollar amount being credited back. Stored
  // as a string while typing so the input stays controlled even when
  // the user clears it; coerced to a Number at save time.
  const [creditAmount, setCreditAmount] = useState('');
  // Supplement-mode only — flat dollar amount to add to the
  // supplier's bill. Stored as a string for the same reason as
  // creditAmount above; coerced to a Number at save time.
  const [supplementAmount, setSupplementAmount] = useState('');
  // Per-row receipt upload state. Map of row-index → status — 'uploading'
  // while the file is in flight, an Error message string on failure.
  // Stored as a flat object so a re-render of one row doesn't disturb
  // sibling rows' upload state.
  const [receiptStatus, setReceiptStatus] = useState({});
  // Per (plantCode|supplierCode) upload status for the written
  // overage-approval file — 'uploading' or an error string.
  const [overageStatus, setOverageStatus] = useState({});
  // Loading flag for the "+ Copy Previous Week" action — drives the
  // button's spinner-state and disables Save / Submit while the fetch
  // is in flight so the user can't kick off two copies. Declared up
  // here with the other state hooks so it never sits behind any of
  // the early-return guards (which would violate React's rules-of-
  // hooks the moment a "no team record" or "no plants" branch runs).
  const [copyingPrev, setCopyingPrev] = useState(false);
  // Per-row collapse state for the Notes / Secondary-Facility panel.
  // Default = collapsed so each entry reads as a single compact line;
  // a chevron in the actions cell expands the panel for that entry.
  // Keyed by absolute row index. Declared up here with the other
  // hooks so it never sits below an early-return guard.
  const [expandedNotes, setExpandedNotes] = useState(() => new Set());
  // Per-plant weekly notes, keyed by plant code ({ FZA: 'note…' }).
  // Loaded from the saved timesheet and written back via PlantNotesJson.
  const [plantNotes, setPlantNotes] = useState({});

  const normEmail = (email || '').trim().toLowerCase();

  // Subject of the editor — whose timesheet we're working on. In the
  // default (submitter) mode this resolves to the signed-in user's
  // own Team record. In RM edit mode it resolves to the *original
  // submitter's* Team record so the plant dropdown stays scoped to
  // their assignments and the saved identity fields stay attached
  // to the right person. Variable name kept as `me` to avoid a
  // sweeping rename of every downstream reference.
  //
  // Edit-mode fallback: if the submitter has been removed from the
  // team list (rare — usually because someone left and was deleted),
  // synthesize a minimal subject from the timesheet's denormalized
  // fields plus the plants referenced in existing rows. This keeps
  // the editor functional even when the submitter's record is gone.
  const me = useMemo(() => {
    if (editing) {
      if (!editingSubmitterId && !timesheet) return null;
      const found = editingSubmitterId
        ? team.find((m) => String(m.id) === String(editingSubmitterId))
        : null;
      if (found) return found;
      // Synthesize from the loaded timesheet. plantCodes pulled from
      // the existing rows so the Plant dropdown shows at least the
      // values already in play.
      if (timesheet) {
        const plantCodesFromRows = Array.from(
          new Set(
            (timesheet.rows || [])
              .map((r) => (r.plantCode || '').trim())
              .filter(Boolean),
          ),
        );
        return {
          id: timesheet.SubmitterId || '',
          Email: timesheet.SubmitterEmail || '',
          Title: timesheet.SubmitterName || '',
          FirstName: '',
          LastName: '',
          RegionalManager: timesheet.RegionalManager || '',
          SubmitsTimesheets: true,
          plantCodes: plantCodesFromRows,
        };
      }
      return null;
    }
    // Adjustment modes (supplement or credit) — subject is the original
    // submitter (from the parent timesheet). Resolve their team record
    // so plant codes are scoped to *their* assignments, not the RM's.
    // Same fallback to a synthetic subject if the team record is
    // missing.
    if (adjusting) {
      if (!parentTimesheet) return null;
      const found = parentTimesheet.SubmitterId
        ? team.find(
            (m) => String(m.id) === String(parentTimesheet.SubmitterId),
          )
        : null;
      if (found) return found;
      const plantCodesFromParentRows = Array.from(
        new Set(
          (parentTimesheet.rows || [])
            .map((r) => (r.plantCode || '').trim())
            .filter(Boolean),
        ),
      );
      return {
        id: parentTimesheet.SubmitterId || '',
        Email: parentTimesheet.SubmitterEmail || '',
        Title: parentTimesheet.SubmitterName || '',
        FirstName: '',
        LastName: '',
        RegionalManager: parentTimesheet.RegionalManager || '',
        SubmitsTimesheets: true,
        plantCodes: plantCodesFromParentRows,
      };
    }
    // Trim both sides — a stray space in the stored Proxemics Email
    // (easy to pick up during data entry / imports) would otherwise
    // silently break the match and show "couldn't find your record".
    return (
      team.find(
        (m) => (m.Email || '').trim().toLowerCase() === normEmail,
      ) || null
    );
  }, [
    team,
    normEmail,
    editing,
    editingSubmitterId,
    timesheet,
    adjusting,
    parentTimesheet,
  ]);

  // ---- Initial fetches -------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [teamRows, plantRows, supplierRows] = await Promise.all([
          listTeam(),
          listPlants(),
          listSuppliers(),
        ]);
        if (cancelled) return;
        setTeam(teamRows || []);
        setPlants(plantRows || []);
        setSuppliers(supplierRows || []);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Load (or reset) the week's timesheet whenever the week picker or
  // identity resolves to a new target. Draft stored on the server wins
  // over in-memory rows so the user doesn't silently lose work when
  // flipping between weeks.
  const loadWeek = useCallback(async () => {
    if (!me?.id || !weekStart) return;
    setSaveError('');
    setSaveNote('');
    try {
      const existing = await getTimesheet(me.id, weekStart);
      if (existing) {
        setTimesheet(existing);
        setRows(
          existing.rows && existing.rows.length ? existing.rows : [emptyRow()],
        );
        setPlantNotes(existing.plantNotes || {});
      } else {
        setTimesheet(null);
        setRows([emptyRow()]);
        setPlantNotes({});
      }
    } catch (err) {
      setError(err);
    }
  }, [me?.id, weekStart]);

  // Edit-mode loader — fetch the target timesheet by id, then sync the
  // week + submitter + rows state from it. Runs in place of the
  // me-based loadWeek above so the RM lands on the exact week the
  // submitter logged. Only kicks off when team is loaded (so the
  // `me` useMemo can resolve the submitter's record after we set
  // editingSubmitterId).
  useEffect(() => {
    if (!editing || !editingId) return;
    let cancelled = false;
    (async () => {
      try {
        const target = await getTimesheetById(editingId);
        if (cancelled || !target) return;
        setTimesheet(target);
        setEditingSubmitterId(target.SubmitterId || '');
        setWeekStart(target.WeekStart || currentWeekStart());
        setRows(
          target.rows && target.rows.length ? target.rows : [emptyRow()],
        );
        setPlantNotes(target.plantNotes || {});
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, editingId]);

  // Adjustment-mode loader — fetch the parent timesheet (for context
  // only; we don't mutate it) so the banner and identity fields know
  // what they're attaching to. Both supplement and credit modes start
  // with an empty grid: a supplement is brand-new data the Director
  // is composing, and a credit is captured purely as a flat dollar
  // amount in the panel below — showing the parent's rows just adds
  // noise to a form that doesn't need them.
  useEffect(() => {
    if (!adjusting || !adjustmentParentId) return;
    let cancelled = false;
    (async () => {
      try {
        const parent = await getTimesheetById(adjustmentParentId);
        if (cancelled || !parent) return;
        setParentTimesheet(parent);
        setWeekStart(parent.WeekStart || currentWeekStart());
        setRows([emptyRow()]);
        setTimesheet(null);
      } catch (err) {
        if (!cancelled) setError(err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [adjusting, adjustmentParentId]);

  useEffect(() => {
    // In edit or adjustment mode the dedicated loaders above own the
    // initial state — skip the "find my own week" path.
    if (editing || adjusting) return;
    loadWeek();
  }, [loadWeek, editing, adjusting]);

  // When the user saves their own draft we hand the freshly-saved record to
  // setTimesheet(), which changes the loaded-timesheet identity and would
  // otherwise retrigger the day-unlock auto-fill below — re-stamping standard
  // hours over days the user just intentionally lowered or zeroed out. This
  // ref lets onSaveDraft flag "this timesheet change is my own save, not a
  // fresh load from the server" so the auto-fill skips exactly that one run.
  // A genuine (re)load via loadWeek never sets it, so day-unlock backfill
  // still happens every time the draft is actually reopened.
  const suppressAutofillRef = useRef(false);

  // ---- Auto-fill standard hours as days unlock -------------------
  // Per Jillian (Aug 2026): once a liaison has started a Billing Tracker
  // and added their suppliers for the week, they shouldn't have to reopen
  // the draft each day and re-type the standard hours. When a new day
  // arrives (unlocks), its cell fills with the plant's standard hours
  // automatically.
  //
  // How it fills — a Standard row's day cell gets seeded when ALL hold:
  //   • the row has a plant + supplier (so a standard is computable),
  //   • the day is now unlocked (not a future date),
  //   • the cell is still empty (0) — we never overwrite a typed value,
  //   • AND one of:
  //       – the row has never been worked (no hours on any unlocked day),
  //         in which case we backfill EVERY unlocked day. This is what
  //         makes a supplier added mid-week (say, on Monday) still fill
  //         Sunday + Monday, and keep filling each day as it unlocks —
  //         regardless of when the draft was last saved; or
  //       – the day only unlocked AFTER the last save (dayIso > the
  //         draft's Modified date). For a row the user is already working,
  //         this fills each newly-unlocked day without touching an
  //         intentional 0 they left on a day they'd already reviewed.
  // Runs whenever a draft (re)loads from the server — the effect keys on
  // the loaded-timesheet identity, so it re-applies on each open/week-flip
  // (and thus each new day) but not on the user's own keystrokes (rows
  // aren't a dependency). New-week drafts with no saved record are handled
  // by the add-row auto-fill instead. Deviations from standard still
  // prompt for an explanation when the user reviews.
  useEffect(() => {
    // The timesheet identity just changed because the user saved their own
    // draft — NOT because a fresh copy loaded from the server. Skip this one
    // run so we never re-stamp standard hours over the values they just
    // curated (an intentional 0 on a day the supplier didn't work, a reduced
    // day, etc.), then clear the flag so the next genuine reopen fills again.
    if (suppressAutofillRef.current) {
      suppressAutofillRef.current = false;
      return;
    }
    if (editing || adjusting) return;
    if (!timesheet || !timesheet.id) return;
    const status = timesheet.Status || STATUS.DRAFT;
    if (!SUBMITTER_EDITABLE_STATUSES.has(status)) return;
    if (!plants.length || !suppliers.length) return;
    const dts = weekDates(weekStart);
    if (!dts.length) return;

    const savedIso = formatLocalDateIso(
      new Date(timesheet.Modified || timesheet.Created || Date.now()),
    );

    setRows((list) => {
      let changed = false;
      const next = list.map((r) => {
        if (r.coverageType !== 'Standard' || !r.plantCode || !r.supplierCode) {
          return r;
        }
        const byDay = computeStandardHoursByDay(
          r.plantCode,
          r.supplierCode,
          plants,
          suppliers,
          r.service,
          dts,
        );
        if (!byDay) return r;
        // Has the user logged any real hours on an unlocked day of this
        // row? If not, it's an untouched row (freshly added, or a prior
        // save that stored zeros for the days that were already unlocked
        // when the supplier was added) — so we backfill every unlocked
        // day rather than only the ones that unlocked since the last save.
        const anyWorked = DAY_KEYS.some((k, i) => {
          const d = dts[i];
          return d && !isFutureDate(d) && Number(r.values[k]) > 0;
        });
        const values = { ...r.values };
        let rowChanged = false;
        const rowPlant = (plants || []).find(
          (pl) => (pl.PlantCode || '') === r.plantCode,
        );
        for (let i = 0; i < DAY_KEYS.length; i += 1) {
          const k = DAY_KEYS[i];
          const date = dts[i];
          if (!date || isFutureDate(date)) continue; // still locked
          // Plant Closure days stay at 0 (per Jillian, Sep 2026) — the
          // gray shading + tooltip still mark the day, and hours can be
          // typed in by hand if someone really worked it.
          if (isPlantClosedOn(rowPlant, date)) continue;
          // Same for days after the supplier's coverage End Date.
          if (rowCoverageEndedOn(r, suppliers, date)) continue;
          if (Number(values[k])) continue; // user already has a value here
          const rounded = Math.round((Number(byDay[k]) || 0) * 100) / 100;
          if (rounded <= 0) continue;
          const dayIso = formatLocalDateIso(date);
          const unlockedSinceSave = dayIso > savedIso;
          // Worked rows: only fill days that unlocked after the last save,
          // preserving any intentional 0 on a day already reviewed.
          // Untouched rows: fill every unlocked day.
          if (anyWorked && !unlockedSinceSave) continue;
          values[k] = rounded;
          rowChanged = true;
        }
        if (rowChanged) {
          changed = true;
          return { ...r, values };
        }
        return r;
      });
      return changed ? next : list;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheet, plants, suppliers, weekStart, editing, adjusting]);

  // Auto-open the Explanation panel for any row that goes over or under
  // its standard hours, so the (required) explanation field is right
  // there without hunting for the chevron (per Jillian, Aug 2026). Only
  // opens on the transition INTO a deviation — the submitter can still
  // collapse it, and a row re-opens if a fresh deviation appears later.
  // MUST live here, above the early returns below, so the hook count is
  // identical on every render (rules-of-hooks). weekStart drives the
  // week's dates, which we compute inside the effect.
  const autoExpandedDeviationRef = useRef(new Set());
  useEffect(() => {
    const effectDates = weekDates(weekStart);
    const seen = autoExpandedDeviationRef.current;
    const toOpen = [];
    rows.forEach((row, idx) => {
      const hasDev = rowDeviates(row, plants, suppliers, effectDates);
      if (hasDev && !seen.has(idx)) {
        seen.add(idx);
        toOpen.push(idx);
      } else if (!hasDev && seen.has(idx)) {
        seen.delete(idx);
      }
    });
    if (toOpen.length) {
      setExpandedNotes((prev) => {
        const next = new Set(prev);
        toOpen.forEach((i) => next.add(i));
        return next;
      });
    }
  }, [rows, plants, suppliers, weekStart]);

  // ---- Auto-seed a brand-new tracker -----------------------------------
  // A fresh Billing Tracker (no saved draft yet) starts pre-populated with
  // a Standard Liaison Hours row for every supplier that's Active or On
  // Call at each plant the submitter is assigned to — hours filled the
  // usual way — so they never have to add plants/suppliers by hand (per
  // Jillian, Aug 2026). Guards:
  //   • never when editing / adjusting someone else's tracker,
  //   • never for Regional Managers or Directors (they start blank),
  //   • never when a saved draft/record already exists (don't clobber it),
  //   • only while the grid is still the untouched empty template.
  // It re-seeds a fresh week on navigation; saved drafts load as-is and
  // rely on the day-unlock auto-fill above.
  useEffect(() => {
    if (editing || adjusting) return;
    // Regional Managers (like Directors) start from a blank tracker and
    // add only the plants / suppliers they need (per Jillian, Nov 2026).
    if (isRegionalManager || isDirector) return;
    if (!me?.id) return;
    if (timesheet && timesheet.id) return; // existing saved draft / record
    if (!plants.length || !suppliers.length) return;
    const untouched =
      rows.length === 0 ||
      (rows.length === 1 &&
        !rows[0].plantCode &&
        !rows[0].supplierCode &&
        (rows[0].coverageType || 'Standard') === 'Standard' &&
        DAY_KEYS.every((k) => !Number(rows[0].values?.[k])));
    if (!untouched) return;
    const myCodes = (Array.isArray(me.plantCodes) ? me.plantCodes : []).filter(
      Boolean,
    );
    if (myCodes.length === 0) return;
    // Liaison Managers only get rows for the suppliers they're responsible
    // for (Sep 2026; see utils/supplierScope.js). Null = not scoped.
    const seedScope = resolveSupplierScope(me, team);
    const dts = weekDates(weekStart);
    const seeded = [];
    const seen = new Set();
    for (const pc of myCodes) {
      const plantUpper = String(pc).toUpperCase();
      for (const s of suppliers) {
        const code = String(s.SupplierCode || '');
        if (!code) continue;
        if (!scopeAllowsSupplierAtPlant(seedScope, plantUpper, code)) continue;
        // One seeded row per (supplier, SERVICE) at this plant — a
        // supplier billing e.g. Standard Coverage AND Lineside Support
        // at the same plant gets two rows, each auto-filled from its
        // own coverage hours (Sep 2026).
        const liveAssignments = assignmentsAtPlant(s, plantUpper).filter(
          (pa) =>
            (pa.coverageStatus === 'Active' || pa.coverageStatus === 'On Call') &&
            // Coverage that ended before this week began isn't seeded.
            !coverageEndedOn(pa, dts[0]),
        );
        for (const pa of liveAssignments) {
          const service = String(pa.service || '');
          const key = `${plantUpper}|${code.toUpperCase()}|${service.toLowerCase()}`;
          if (seen.has(key)) continue;
          seen.add(key);
          const base = emptyRow('Standard');
          const values = { ...base.values };
          const byDay = computeStandardHoursByDay(pc, code, plants, suppliers, service, dts);
          if (byDay) {
            const seedPlant = (plants || []).find((pl) => (pl.PlantCode || '') === pc);
            for (let i = 0; i < DAY_KEYS.length; i += 1) {
              const k = DAY_KEYS[i];
              const date = dts[i];
              if (!date || isFutureDate(date)) continue;
              // Plant Closure days seed at 0 (per Jillian, Sep 2026).
              if (isPlantClosedOn(seedPlant, date)) continue;
              // Days after the coverage End Date seed at 0 too.
              if (coverageEndedOn(pa, date)) continue;
              const rounded = Math.round((Number(byDay[k]) || 0) * 100) / 100;
              if (rounded > 0) values[k] = rounded;
            }
          }
          seeded.push({ ...base, plantCode: pc, supplierCode: code, service, values });
        }
      }
    }
    if (seeded.length > 0) setRows(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheet, plants, suppliers, weekStart, rows, me, team, editing, adjusting, isRegionalManager, isDirector]);

  // Director retract-and-edit of an Approved tracker (Nov 2026).
  const retracting = editing && timesheet?.Status === STATUS.APPROVED;
  useEffect(() => {
    if (!retracting || !timesheet?.id) {
      setInvoicedOn('');
      return undefined;
    }
    let cancelled = false;
    listInvoices()
      .then((invoices) => {
        if (cancelled) return;
        const id = String(timesheet.id);
        if (!billedTimesheetIds(invoices).has(id)) {
          setInvoicedOn('');
          return;
        }
        const inv = (invoices || []).find((i) =>
          (i.timesheetIds || []).includes(id) &&
          i.Status !== 'Draft' &&
          i.Status !== 'Cancelled',
        );
        setInvoicedOn(inv?.InvoiceNumber || 'an existing invoice');
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [retracting, timesheet?.id]);

  // ---- Access gating ---------------------------------------------
  // (see also the Director-only gate for Approved trackers below)
  // Loading state comes first so a blank team list doesn't briefly flash
  // the "not enrolled" empty state while the initial fetch is in flight.
  if (loading) return <Loading />;
  if (error) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Couldn't Load Billing Tracker</div>
        <p>{error.message || 'Unknown error.'}</p>
      </div>
    );
  }
  // Edit / adjustment gate: only Directors and Regional Managers may
  // act on someone else's timesheet. Surface a clear refusal instead
  // of silently rendering the submitter UI for the wrong person.
  if ((editing || adjusting) && !(isDirector || isRegionalManager)) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Billing Tracker</div>
        <p className="muted">
          Only Regional Managers and Directors can edit, supplement, or
          credit a submitted timesheet.
        </p>
      </div>
    );
  }
  // In edit/adjustment mode the parent (or target) timesheet may still
  // be loading even though the initial team / plants / suppliers fetch
  // is done — render a lightweight placeholder until it resolves so we
  // don't briefly flash the submitter's "no plants" panel.
  if (editing && (!timesheet || !me)) {
    return <Loading />;
  }
  // Only Directors may retract and edit an Approved tracker (Nov 2026).
  if (retracting && !isDirector) {
    return (
      <div className="card" style={{ borderColor: 'var(--brand-danger)' }}>
        <div className="card-title">Billing Tracker</div>
        <p className="muted">
          Only Directors can retract and edit an approved Billing Tracker.
        </p>
      </div>
    );
  }
  if (adjusting && (!parentTimesheet || !me)) {
    return <Loading />;
  }
  if (!editing && !adjusting && !me) {
    return (
      <div className="card">
        <div className="card-title">Billing Tracker</div>
        <p className="muted">
          We couldn't find your Proxemics Team record. Ask a Director to add
          you to the team list — timesheets key off that record, so you
          can't submit hours until it exists.
        </p>
      </div>
    );
  }
  // Regional Managers and Directors can always submit (for the plants they're
  // assigned over — see plantOptions below). Everyone else needs the "Submits
  // Billing Tracker" flag ticked on their Team record.
  if (
    !editing &&
    !adjusting &&
    !me.SubmitsTimesheets &&
    !isDirector &&
    !isRegionalManager
  ) {
    return (
      <div className="card">
        <div className="card-title">Billing Tracker</div>
        <p className="muted">
          Your profile isn't set to submit Billing Tracker entries. If this
          is wrong, ask a Director to tick "Submits Billing Tracker" on your
          Team record.
        </p>
      </div>
    );
  }
  // If the team record has no plantCodes, the Plant dropdown would be
  // empty and the grid would be useless. Surface a clearer message so
  // the person knows where to fix it rather than staring at "— select —"
  // with nothing to pick. In edit mode we tolerate a missing list
  // because the timesheet's existing rows still need to render even
  // if the submitter has since been unassigned from their plants.
  if (
    !editing &&
    !adjusting &&
    !isDirector &&
    (!Array.isArray(me.plantCodes) || me.plantCodes.length === 0)
  ) {
    return (
      <div className="card">
        <div className="card-title">Billing Tracker</div>
        <p className="muted">
          You don't have any plants assigned to you yet. Ask a Director to
          add one or more plants to your Team record — the timesheet grid
          keys off that list.
        </p>
      </div>
    );
  }

  // ---- Derived context for the grid ------------------------------
  const status = timesheet?.Status || STATUS.DRAFT;
  const submitterEditable = SUBMITTER_EDITABLE_STATUSES.has(status);
  // Once a timesheet is Approved or Pending, the Timesheets page is
  // purely read-only — no Save Draft, no + Add Entry, no row editing.
  // Edit-mode overrides this: the RM is explicitly here to correct
  // the submitted hours, so the grid stays editable regardless of
  // status (Pending is the realistic case; Approved drops in via the
  // separate updateApprovedTimesheet path). Supplement mode is also
  // editable — the user is composing a brand-new row set. Credit
  // mode keeps the grid read-only: the credit is captured as a flat
  // dollar amount in the panel below, and the parent rows shown
  // above are reference-only context.
  const canEdit = editing || supplementing || submitterEditable;
  const canSubmit = submitterEditable && rows.some(rowIsNonEmpty);

  // Plant dropdown scoping. Regional / Liaison Managers (and anyone
  // else who submits) are scoped to the plants on their own team
  // record — they can only bill their own plants/suppliers. Directors
  // are the exception: they can submit / edit a Billing Tracker for ANY
  // plant and supplier, so they see the full plant list regardless of
  // their own assignments.
  const myPlantCodes = Array.isArray(me.plantCodes) ? me.plantCodes : [];
  const plantOptions = isDirector
    ? plants
    : plants.filter((p) => myPlantCodes.includes(p.PlantCode || ''));
  // Plain index build — computed after early returns, so using useMemo()
  // here would violate React's rules-of-hooks (hooks must run in the same
  // order every render). The plants list is small enough that rebuilding
  // this Map per render is cheap.
  const plantByCode = new Map();
  for (const p of plants) plantByCode.set(p.PlantCode, p);

  const dates = weekDates(weekStart);
  // Whether this week's tracker can be SUBMITTED yet — opens on the
  // week-ending Saturday. Saving a draft is always allowed. RM/Director
  // edit and adjustment flows act on already-submitted entries (a
  // different button), so they're not gated by this.
  const submitWindow = weekSubmitWindow(dates);
  // Suppliers that require written approval when they go over standard —
  // drives the per-plant upload controls and the submit-time hard block.
  const overageNeeds = overageApprovalNeeds(rows, suppliers, plants, dates);
  const totals = computeTotals(rows);

  // Every day of the tracker week's month, for the Mileage / Expenses
  // "Day" dropdowns — the submitter can attribute an entry to any day of
  // the current month, not just the seven days of this week (per Jillian,
  // Aug 2026). Each option carries the full ISO date; labels read like
  // "Mon, 8/3".
  const monthDays = (() => {
    // Day-picker options for Mileage / Expenses. The billing week runs
    // Sun→Sat, so a week can straddle a month boundary (e.g. Aug 30 –
    // Sep 5). Include ALL days of EVERY calendar month the week touches
    // — one month for a normal week, both months for a straddling week —
    // so the dropdown never hides the days that fall in the next month.
    const start = new Date(`${weekStart}T00:00:00`);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    const months = [];
    const seen = new Set();
    for (const anchor of [start, end]) {
      const key = `${anchor.getFullYear()}-${anchor.getMonth()}`;
      if (seen.has(key)) continue;
      seen.add(key);
      months.push({ y: anchor.getFullYear(), m: anchor.getMonth() });
    }
    const out = [];
    for (const { y, m } of months) {
      const lastDay = new Date(y, m + 1, 0).getDate();
      for (let d = 1; d <= lastDay; d += 1) {
        const dt = new Date(y, m, d);
        const iso = `${y}-${String(m + 1).padStart(2, '0')}-${String(
          d,
        ).padStart(2, '0')}`;
        out.push({
          iso,
          label: dt.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'numeric',
            day: 'numeric',
          }),
        });
      }
    }
    // Chronological order so the second month's days follow the first.
    out.sort((a, b) => a.iso.localeCompare(b.iso));
    return out;
  })();
  // Map an ISO date to the DAY_KEYS weekday slot it falls on. Mileage
  // miles are stored in that weekday cell of the row's value grid so the
  // billing engine (which reads the grid) keeps working unchanged; the
  // chosen ISO date rides along in row.mileageDate for display.
  const dayKeyFromIso = (iso) => {
    if (!iso) return '';
    const wd = new Date(`${iso}T00:00:00`).getDay(); // 0=Sun … 6=Sat
    return ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'][wd];
  };

  // (The auto-open-on-deviation effect lives higher up with the other
  // hooks — it must run before the early returns below to satisfy
  // React's rules-of-hooks.)
  // Full-record lookup the expense row renderer uses to decide whether
  // to show a "Required" tag next to the receipt control — see
  // expenseRowRequiresReceipt above for the rule.
  const supplierByCode = new Map(
    (suppliers || []).map((s) => [
      String(s.SupplierCode || '').trim().toUpperCase(),
      s,
    ]),
  );

  // Weekly-billing row helpers, shared by both HoursTable stacks and
  // the Mileage / Expenses tables. rowIsWeekly drives the light-orange
  // highlight; rowTotalColor colors a Standard row's weekly TOTAL when
  // the week is off standard (blue under / red over).
  const rowIsWeekly = (row) =>
    supplierPlantBillsWeekly(row.plantCode, row.supplierCode, suppliers, row.service, dates);
  const rowTotalColor = (row) => {
    const d = weeklyStandardDeviation(row, plants, suppliers, dates);
    if (!d) return undefined;
    return d.dir === 'under' ? STD_UNDER_COLOR : STD_OVER_COLOR;
  };
  // Distinct weekly-billing (supplier, plant) pairs present in the grid,
  // used to render the "Suppliers that Bill Weekly" key beneath the
  // tracker. Blank template rows never match (rowIsWeekly needs a plant
  // AND supplier), so the key only lists real entries.
  const weeklyLegendItems = (() => {
    const seen = new Set();
    const items = [];
    rows.forEach((r) => {
      if (!rowIsWeekly(r)) return;
      const dedupeKey = `${r.supplierCode}|${r.plantCode}|${String(r.service || '').toLowerCase()}`;
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      const sup = supplierByCode.get(String(r.supplierCode).toUpperCase());
      const svc = serviceLabel(findAssignment(sup, r.plantCode, r.service));
      items.push({
        supplier: (sup?.Title || r.supplierCode) + (svc ? ` — ${svc}` : ''),
        plant: r.plantCode,
      });
    });
    items.sort((a, b) =>
      `${a.supplier} ${a.plant}`.localeCompare(`${b.supplier} ${b.plant}`),
    );
    return items;
  })();

  // ---- Row mutations ---------------------------------------------
  const setRow = (index, patch) =>
    setRows((list) =>
      list.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  /**
   * setRow variant that also auto-fills the Mon–Fri hours when the row
   * becomes a valid Standard / Plant / Supplier combination with empty
   * day cells. Pulls the "Total Hours/Day" off the supplier's Plant
   * Locations & Coverage row so the timesheet starts from the same
   * number Proxemics bills with, then lets the user edit any day.
   * Guards against overwriting real work: if ANY day already has a
   * non-zero value, the row is left alone.
   */
  const setRowWithAutofill = (index, patch) => {
    setRows((list) =>
      list.map((r, i) => {
        if (i !== index) return r;
        let next = { ...r, ...patch };
        // Zero the day cells whenever the row's identity changes —
        // Coverage Type, Plant, or Supplier. Each of those is the
        // "what is this row" key; switching any of them means the
        // hours that were there belonged to the *previous*
        // (plant, supplier, type) tuple and should be discarded. This
        // also lets the autofill block below fire its "only when
        // cells are empty" guard for the freshly-picked combination
        // so the new supplier's allocation lands in the grid right
        // away instead of needing a page refresh.
        const typeChanged =
          patch.coverageType && patch.coverageType !== r.coverageType;
        const plantChanged =
          patch.plantCode !== undefined && patch.plantCode !== r.plantCode;
        const supplierChanged =
          patch.supplierCode !== undefined &&
          patch.supplierCode !== r.supplierCode;
        const serviceChanged =
          patch.service !== undefined && patch.service !== r.service;
        if (typeChanged || plantChanged || supplierChanged || serviceChanged) {
          next = {
            ...next,
            values: {
              mon: 0,
              tue: 0,
              wed: 0,
              thu: 0,
              fri: 0,
              sat: 0,
              sun: 0,
            },
          };
        }
        const allDaysEmpty = DAY_KEYS.every(
          (k) => !Number(next.values[k]),
        );
        if (
          next.coverageType === 'Standard' &&
          next.plantCode &&
          next.supplierCode &&
          allDaysEmpty
        ) {
          // Day-of-week aware auto-fill. computeStandardHoursByDay
          // returns per-day values that already account for the
          // plant's weekday / Saturday / Sunday shift counts. We keep
          // the exact hours (rounded
          // only to 2 decimals to avoid float drift, no 15-minute
          // snapping), and skip future dates (the timesheet logs
          // completed work, not a forecast). Days with 0 expected hours
          // are left at 0 — the user already chose "Zero them out" for
          // days the plant doesn't run.
          const byDay = computeStandardHoursByDay(
            next.plantCode,
            next.supplierCode,
            plants,
            suppliers,
            next.service,
            dates,
          );
          if (byDay) {
            const weekDates = dates;
            const values = { ...next.values };
            let anyFilled = false;
            const rowPlant = (plants || []).find(
              (pl) => (pl.PlantCode || '') === next.plantCode,
            );
            for (let i = 0; i < DAY_KEYS.length; i += 1) {
              const k = DAY_KEYS[i];
              const date = weekDates[i];
              if (!date || isFutureDate(date)) continue;
              // Plant Closure days autofill to 0 (per Jillian, Sep 2026),
              // and so do days after the supplier's coverage End Date.
              if (isPlantClosedOn(rowPlant, date) || rowCoverageEndedOn(next, suppliers, date)) {
                values[k] = 0;
                continue;
              }
              const raw = Number(byDay[k]) || 0;
              const rounded = Math.round(raw * 100) / 100;
              values[k] = rounded;
              if (rounded > 0) anyFilled = true;
            }
            // Only commit the auto-fill when at least one day picked
            // up a non-zero number. An all-zeros result usually
            // means the plant's shift counts haven't been set yet,
            // in which case the liaison is better off with empty
            // cells they can type into themselves.
            if (anyFilled) return { ...next, values };
          }
        }
        return next;
      }),
    );
  };
  const setDayValue = (index, dayKey, raw) => {
    const v = parseHoursExact(raw);
    setRows((list) =>
      list.map((r, i) =>
        i === index ? { ...r, values: { ...r.values, [dayKey]: v } } : r,
      ),
    );
  };
  // addRow optionally takes a coverageType so each category card's
  // "Add row" button creates a row already classified as Hours
  // (Standard), Mileage, or Expenses. Without an arg it defaults to a
  // Standard hours row, matching emptyRow's default — preserves
  // backwards-compat with any caller that didn't expect the arg.
  const addRow = (coverageType, plantCode = '') =>
    setRows((list) => [...list, { ...emptyRow(coverageType), plantCode }]);
  const removeRow = (index) =>
    setRows((list) => (list.length === 1 ? list : list.filter((_, i) => i !== index)));
  const toggleNotes = (index) =>
    setExpandedNotes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  /**
   * Pull the prior week's timesheet for this submitter and stamp its
   * rows onto the current grid. Receipts are intentionally cleared on
   * copy — each week's expense receipts are unique to that week, and
   * the canonical filename pattern bakes the week's date in. The user
   * still has to re-attach a fresh receipt before submitting.
   *
   * Behavior:
   *   - If the previous week has no saved timesheet → show a note and stop.
   *   - If the current grid has any non-empty rows → confirm overwrite
   *     before clobbering.
   *   - Otherwise → replace the grid wholesale with the prior week's
   *     rows (minus receipts).
   */
  const onCopyPreviousWeek = async () => {
    if (!me?.id) return;
    setSaveError('');
    setSaveNote('');
    setCopyingPrev(true);
    try {
      const prevWeek = shiftWeek(weekStart, -1);
      const prev = await getTimesheet(me.id, prevWeek);
      if (!prev || !Array.isArray(prev.rows) || prev.rows.length === 0) {
        setSaveNote(
          `No timesheet found for the week of ${prevWeek}. Nothing to copy.`,
        );
        return;
      }
      // Skip Expenses rows when copying. Expenses are by definition
      // one-off items tied to that week (the receipt + dollar amount
      // belong to a specific spend, not a recurring pattern), so
      // pulling them forward would force the user to delete each
      // stale row before submitting. Hours / Mileage / Standard /
      // Overage / Other rows still copy through.
      const filtered = prev.rows.filter(
        (r) => (r.coverageType || 'Standard') !== 'Expenses',
      );
      const skippedExpenses = prev.rows.length - filtered.length;
      if (filtered.length === 0) {
        setSaveNote(
          `Last week's Billing Tracker only had Expenses entries, which aren't copied. Nothing to bring forward.`,
        );
        return;
      }
      const currentHasData = rows.some(rowIsNonEmpty);
      if (currentHasData) {
        const ok = window.confirm(
          'This will replace everything currently on this week with the entries from last week. Continue?',
        );
        if (!ok) return;
      }
      // Strip receipt attachments defensively — week-specific. Keep
      // plant / supplier / coverage type / hours / explanation.
      const cloned = filtered.map((r) => ({
        ...r,
        receiptFileName: '',
        receiptUrl: '',
      }));
      setRows(cloned);
      setReceiptStatus({});
      const expensesNote =
        skippedExpenses > 0
          ? ` (${skippedExpenses} Expenses ${skippedExpenses === 1 ? 'entry was' : 'entries were'} skipped — those need to be re-entered each week.)`
          : '';
      setSaveNote(
        `Copied ${cloned.length} ${cloned.length === 1 ? 'entry' : 'entries'} from the week of ${prevWeek}.${expensesNote}`,
      );
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[timesheets] copy previous week failed', err);
      setSaveError(
        `Couldn't copy last week's timesheet: ${err?.message || err}`,
      );
    } finally {
      setCopyingPrev(false);
    }
  };

  /**
   * Upload an expense receipt for one row. The upload helper takes care
   * of the canonical filename rebuild (TeamMember-Plant-Supplier-
   * ExpenseReceipt-WeekStart.ext) so the SharePoint library is
   * directly browsable for finance audits. We stash the resulting
   * fileName + webUrl on the row so the grid (and the RM approvals
   * page) can render the filename as a target=_blank link.
   *
   * The upload runs independently of the timesheet's main save flow —
   * the row gets updated in memory immediately, and the next Save Draft
   * round-trips the link into RowsJson on SharePoint.
   */
  const onUploadReceipt = async (index, file) => {
    if (!file) return;
    setReceiptStatus((prev) => ({ ...prev, [index]: 'uploading' }));
    const row = rows[index];
    try {
      const result = await uploadTimesheetReceipt({
        file,
        teamMemberName:
          me.Title ||
          [me.FirstName, me.LastName].filter(Boolean).join(' ') ||
          'Unknown',
        plantCode: row?.plantCode || '',
        supplierCode: row?.supplierCode || '',
        weekStart,
        postedBy: name || email || '',
      });
      setRow(index, {
        receiptFileName: result.fileName,
        receiptUrl: result.webUrl,
      });
      setReceiptStatus((prev) => {
        const next = { ...prev };
        delete next[index];
        return next;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[timesheets] receipt upload failed', err);
      setReceiptStatus((prev) => ({
        ...prev,
        [index]:
          err?.message ||
          'Upload failed. Try again, or check the browser console.',
      }));
    }
  };

  /**
   * Upload a written overage-approval file for one (plant, supplier).
   * Anchored on the pair's Standard row (anchorIdx) so there's one file
   * per (supplier, plant, week). Stashes fileName + webUrl on that row's
   * overageApprovalFileName / overageApprovalUrl, round-tripped into
   * RowsJson on the next Save.
   */
  const onUploadOverageApproval = async (
    anchorIdx,
    plantCode,
    supplierCode,
    file,
  ) => {
    if (!file) return;
    const key = `${plantCode}|${supplierCode}`;
    setOverageStatus((prev) => ({ ...prev, [key]: 'uploading' }));
    try {
      const result = await uploadTimesheetOverageApproval({
        file,
        teamMemberName:
          me.Title ||
          [me.FirstName, me.LastName].filter(Boolean).join(' ') ||
          'Unknown',
        plantCode: plantCode || '',
        supplierCode: supplierCode || '',
        weekStart,
        postedBy: name || email || '',
      });
      setRow(anchorIdx, {
        overageApprovalFileName: result.fileName,
        overageApprovalUrl: result.webUrl,
      });
      setOverageStatus((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('[timesheets] overage-approval upload failed', err);
      setOverageStatus((prev) => ({
        ...prev,
        [key]:
          err?.message ||
          'Upload failed. Try again, or check the browser console.',
      }));
    }
  };

  // ---- Save path -------------------------------------------------
  async function buildModel(baseStatus) {
    // Keep the denormalized submitter fields in sync in case someone's
    // name changed on the roster between drafts.
    return {
      id: timesheet?.id,
      Title: `${me.Title || [me.FirstName, me.LastName].filter(Boolean).join(' ')} — ${weekStart}`.trim(),
      SubmitterId: me.id || '',
      SubmitterEmail: me.Email || normEmail,
      SubmitterName:
        me.Title || [me.FirstName, me.LastName].filter(Boolean).join(' '),
      RegionalManager: me.RegionalManager || '',
      WeekStart: weekStart,
      Status: baseStatus,
      rows,
      plantNotes,
    };
  }

  const onSaveDraft = async () => {
    setSaving(true);
    setSaveError('');
    setSaveNote('');
    try {
      const model = await buildModel(STATUS.DRAFT);
      const saved = await saveDraftTimesheet(model);
      // Mark this as a save-driven timesheet change so the day-unlock
      // auto-fill effect skips the run that setTimesheet() is about to
      // trigger — otherwise it would overwrite the exact hours we just
      // saved with the plant's standard hours. See suppressAutofillRef.
      suppressAutofillRef.current = true;
      setTimesheet(saved);
      setSaveNote('Draft saved.');
    } catch (err) {
      setSaveError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Supplement-mode: create a new Pending timesheet linked to the
  // parent. The RM/Director provides the Reason and BillingDisposition
  // (both required) so the audit trail and the eventual invoice line
  // carry the context. Same row-validation as the regular Submit path
  // so half-filled rows don't sneak through.
  const onSaveSupplement = async () => {
    if (!parentTimesheet) {
      setSaveError('Parent timesheet not loaded.');
      return;
    }
    if (!supplementReason.trim()) {
      setSaveError('Please add a short reason for this supplement.');
      setSaveNote('');
      return;
    }
    if (!supplementDisposition) {
      setSaveError('Pick how this supplement should be billed.');
      setSaveNote('');
      return;
    }
    const supplementAmountNum = Number(supplementAmount);
    if (!Number.isFinite(supplementAmountNum) || supplementAmountNum <= 0) {
      setSaveError('Enter a Supplement Amount greater than zero.');
      setSaveNote('');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveNote('');
    try {
      // Supplements save as rowless adjustment records — same shape
      // as credits. Reason + Disposition + flat dollar
      // SupplementAmount are the meaningful payload; the Director
      // picks the line up on the invoice draft at generation time.
      const base = await buildModel(STATUS.PENDING);
      await createAdjustmentTimesheet({
        ...base,
        rows: [],
        AdjustmentType: ADJUSTMENT_TYPES.SUPPLEMENT,
        OriginalTimesheetId: String(parentTimesheet.id),
        AdjustmentReason: supplementReason.trim(),
        BillingDisposition: supplementDisposition,
        SupplementAmount: supplementAmountNum,
      });
      // Back to the Approvals tab so the new Pending supplement
      // shows up in the queue immediately.
      navigate('/timesheets/approvals');
    } catch (err) {
      setSaveError(err.message || 'Save failed.');
      setSaving(false);
    }
  };

  // Credit-mode: create a new Pending credit timesheet linked to the
  // parent. Flat dollar amount + reason; the row grid the user sees
  // above is the parent's billed rows shown for reference. We zero
  // out the rows on the saved record because credits are stored as
  // a flat CreditAmount — keeping the parent's hours on the credit
  // record would double-count them when aggregateBilling runs.
  const onSaveCredit = async () => {
    if (!parentTimesheet) {
      setSaveError('Parent timesheet not loaded.');
      return;
    }
    if (!supplementReason.trim()) {
      setSaveError('Please add a short reason for this credit.');
      setSaveNote('');
      return;
    }
    if (!supplementDisposition) {
      setSaveError('Pick how this credit should be issued.');
      setSaveNote('');
      return;
    }
    const amountNum = Number(creditAmount);
    if (!Number.isFinite(amountNum) || amountNum <= 0) {
      setSaveError(
        'Enter a positive dollar amount for the credit (e.g. 450.00).',
      );
      setSaveNote('');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveNote('');
    try {
      // Use buildModel for identity / week / submitter denorm, but
      // zero the rows so the credit record doesn't drag the parent's
      // billed hours into billing aggregation. CreditAmount + Reason
      // + Disposition carry the meaningful payload.
      const base = await buildModel(STATUS.PENDING);
      await createAdjustmentTimesheet({
        ...base,
        rows: [],
        AdjustmentType: ADJUSTMENT_TYPES.CREDIT,
        OriginalTimesheetId: String(parentTimesheet.id),
        AdjustmentReason: supplementReason.trim(),
        BillingDisposition: supplementDisposition,
        CreditAmount: amountNum,
      });
      navigate('/timesheets/approvals');
    } catch (err) {
      setSaveError(err.message || 'Save failed.');
      setSaving(false);
    }
  };

  // Edit-mode: RM persists their edits and keeps the status as
  // Pending so it (or another approver) can review the corrected
  // version separately. Same validation as the submitter's Submit
  // path so we don't accept a half-filled row just because the
  // editor is an admin.
  const onSavePendingFromEdit = async () => {
    const missing = validateRowsForSubmit(rows, suppliers, plants, dates);
    if (missing.length > 0) {
      setSaveError(`Fix these before saving:\n• ${missing.join('\n• ')}`);
      setSaveNote('');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveNote('');
    try {
      const model = await buildModel(STATUS.PENDING);
      // Same rule as Submit: zero-hour supplier rows don't survive.
      model.rows = stripEmptyRows(model.rows);
      if (retracting) {
        // Retracted Approved tracker → overwrite it and send it back to
        // the Approvals queue as Pending, clearing the old approval stamp.
        await updatePendingTimesheet(timesheet.id, model, { clearApproval: true });
        navigate('/timesheets/approvals');
        return;
      }
      const saved = await updatePendingTimesheet(timesheet.id, model);
      setTimesheet(saved);
      setSaveNote('Saved. Status kept as Pending.');
    } catch (err) {
      setSaveError(err.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  // Edit-mode: RM persists their edits and approves in one click.
  // We save first so the approved record reflects the edited values,
  // then run the same approval call the Approve button uses.
  const onSaveAndApproveFromEdit = async () => {
    const missing = validateRowsForSubmit(rows, suppliers, plants, dates);
    if (missing.length > 0) {
      setSaveError(`Fix these before approving:\n• ${missing.join('\n• ')}`);
      setSaveNote('');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveNote('');
    try {
      const model = await buildModel(STATUS.PENDING);
      if (retracting) model.rows = stripEmptyRows(model.rows);
      await updatePendingTimesheet(timesheet.id, model);
      await approveTimesheet(timesheet.id, name || email || '');
      // Back to the Approvals page — the timesheet is now in the
      // Approved section, so dropping the RM there closes the loop.
      navigate('/timesheets/approvals');
    } catch (err) {
      setSaveError(err.message || 'Approve failed.');
      setSaving(false);
    }
  };

  const onSubmit = async () => {
    // A tracker can't be submitted before its week has ended — submission
    // opens on the week-ending Saturday. Saving a draft stays available,
    // so a week in progress can't be submitted early by accident. (The
    // Submit button is also disabled in this window; this guard is the
    // backstop.)
    if (!submitWindow.open) {
      setSaveError(
        `You can't submit this week's Billing Tracker yet — submitting opens on ${submitWindow.opensLabel}, the end of the week. You can keep saving it as a draft until then.`,
      );
      setSaveNote('');
      return;
    }
    // Run required-field validation before saving. Empty rows are
    // ignored (they're just unused templates); rows with any content
    // have to have Plant, Supplier, a non-zero value, and an Explanation
    // when the coverage type calls for one.
    const missing = validateRowsForSubmit(rows, suppliers, plants, dates);
    if (missing.length > 0) {
      setSaveError(
        `Fix these before submitting:\n• ${missing.join('\n• ')}`,
      );
      setSaveNote('');
      return;
    }
    setSaving(true);
    setSaveError('');
    setSaveNote('');
    try {
      // Save first so the submitted snapshot is exactly what's on screen,
      // minus any supplier rows with no hours / no data — those are
      // untouched templates and are dropped at submit (per Jillian, Sep
      // 2026) so they never reach Approvals, billing, or an invoice.
      const model = await buildModel(STATUS.DRAFT);
      model.rows = stripEmptyRows(model.rows);
      const saved = await saveDraftTimesheet(model);
      const submitted = await submitTimesheetForApproval(saved.id);
      setTimesheet(submitted);
      setSaveNote('Submitted — waiting on your Regional Manager to approve.');
    } catch (err) {
      setSaveError(err.message || 'Submit failed.');
    } finally {
      setSaving(false);
    }
  };

  // RM-only actions (surfaced when the current user can edit an approved
  // row OR when viewing someone else's pending row, which this page
  // doesn't support yet — that's the Approval inbox, separate task).

  // ---- Render ----------------------------------------------------
  return (
    <>
      <div className="page-header">
        <h1>
          {editing
            ? retracting
              ? 'Edit Approved Billing Tracker Entry'
              : 'Edit Submitted Billing Tracker Entry'
            : supplementing
              ? 'Add Supplement to Previously Billed Entry'
              : crediting
                ? 'Issue Credit Against Previously Billed Entry'
                : 'Weekly Billing Tracker'}
        </h1>
        {editing || adjusting ? (
          // RM edit / adjustment mode — week navigation doesn't apply
          // (the action is pinned to a single parent timesheet), so the
          // action row just gets a "Back to Approvals" link. Save/cancel
          // buttons live in the footer once the grid loads.
          <div className="actions">
            <button
              type="button"
              className="btn btn-outline"
              onClick={() => navigate('/timesheets/approvals')}
            >
              ← Back to Approvals
            </button>
          </div>
        ) : (
          <div className="actions">
            {/* Previous Week stops at the Billing Tracker's first open
                week (June 22, 2026) — there are no weeks before that to
                log against. */}
            <button
              className="btn btn-outline"
              onClick={() => setWeekStart((w) => shiftWeek(w, -1))}
              disabled={saving || weekStart <= BILLING_TRACKER_FIRST_WEEK}
              title={
                weekStart <= BILLING_TRACKER_FIRST_WEEK
                  ? 'The Billing Tracker starts the week of June 22, 2026.'
                  : ''
              }
            >
              ← Previous Week
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setWeekStart(defaultWeekStart())}
              disabled={saving || weekStart === defaultWeekStart()}
            >
              Current Week
            </button>
            {/* Next Week is blocked at the current week — timesheets are a
                log of work done, not a forecast, so there's no such thing
                as a "future timesheet" to navigate to. (Until the pilot
                starts, defaultWeekStart parks on the first pilot week and
                both directions are locked.) */}
            <button
              className="btn btn-outline"
              onClick={() => setWeekStart((w) => shiftWeek(w, 1))}
              disabled={saving || weekStart >= defaultWeekStart()}
              title={
                weekStart >= defaultWeekStart()
                  ? "You can't log hours for a future week."
                  : ''
              }
            >
              Next Week →
            </button>
          </div>
        )}
      </div>

      {editing && timesheet && (
        // Banner — tells the RM exactly whose timesheet they're editing
        // and for which week, so the page can't be confused with their
        // own. Uses the soft-orange brand surface to read as an admin
        // action rather than a regular page.
        <div
          className="card"
          style={{
            marginBottom: 16,
            background: 'var(--brand-primary-soft, #fff4e6)',
            borderColor: 'var(--brand-primary, #f06a25)',
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: 'var(--brand-primary-dark, #b14a14)',
            }}
          >
            Editing{' '}
            <strong>
              {timesheet.SubmitterName || 'this submitter'}
            </strong>
            's Billing Tracker for{' '}
            <strong>
              {timesheet.WeekStart
                ? `week of ${formatWeekRange(weekDates(timesheet.WeekStart))}`
                : 'an unknown week'}
            </strong>
            . Current status: <strong>{timesheet.Status || 'Pending'}</strong>.
          </div>
          {retracting && (
            <div
              style={{
                fontSize: 13,
                color: 'var(--brand-primary-dark, #b14a14)',
                marginTop: 8,
              }}
            >
              Your changes replace the approved version when you save.{' '}
              <strong>Save &amp; Re-Approve</strong> keeps it approved;{' '}
              <strong>Save &amp; Resubmit for Approval</strong> sends it back to
              the Approvals queue. Cancel leaves the approved version as it is.
              {invoicedOn && (
                <div style={{ marginTop: 6, color: 'var(--brand-danger, #dc2626)' }}>
                  This tracker is already on invoice <strong>{invoicedOn}</strong>.
                  Editing it here won't change that invoice.
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {supplementing && parentTimesheet && (
        // Supplement banner — explains that the user is adding a new,
        // separate timesheet linked back to the parent. Same soft-orange
        // treatment as edit mode so the page reads consistently as an
        // admin action.
        <div
          className="card"
          style={{
            marginBottom: 16,
            background: 'var(--brand-primary-soft, #fff4e6)',
            borderColor: 'var(--brand-primary, #f06a25)',
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: 'var(--brand-primary-dark, #b14a14)',
            }}
          >
            Adding a <strong>supplement</strong> to{' '}
            <strong>
              {parentTimesheet.SubmitterName || 'this submitter'}
            </strong>
            's Billing Tracker for{' '}
            <strong>
              {parentTimesheet.WeekStart
                ? `week of ${formatWeekRange(weekDates(parentTimesheet.WeekStart))}`
                : 'an unknown week'}
            </strong>
            .
          </div>
        </div>
      )}

      {crediting && parentTimesheet && (
        // Credit banner — matches the supplement banner's orange
        // treatment for visual consistency between the two adjustment
        // flows. The Credit pill on Approval cards and the credits
        // sub-table on the Billing dashboard still use blue to
        // distinguish saved credits from saved supplements after the
        // fact; only the in-flight editor banner is unified.
        <div
          className="card"
          style={{
            marginBottom: 16,
            background: 'var(--brand-primary-soft, #fff4e6)',
            borderColor: 'var(--brand-primary, #f06a25)',
          }}
        >
          <div
            style={{
              fontSize: 13,
              color: 'var(--brand-primary-dark, #b14a14)',
            }}
          >
            Issuing a <strong>credit</strong> against{' '}
            <strong>
              {parentTimesheet.SubmitterName || 'this submitter'}
            </strong>
            's Billing Tracker for{' '}
            <strong>
              {parentTimesheet.WeekStart
                ? `week of ${formatWeekRange(weekDates(parentTimesheet.WeekStart))}`
                : 'an unknown week'}
            </strong>
            .
          </div>
        </div>
      )}

      <div className="card">
        {/* Week header + submission deadline + status badge. Hidden
            in adjustment modes (supplement + credit) because the
            adjustment banner above already names the submitter and
            the week, and the deadline / status badge / week date
            don't apply to a flat adjustment entry. */}
        {!adjusting && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 16,
              flexWrap: 'wrap',
            }}
          >
            <div>
              <div className="card-title" style={{ fontSize: 22 }}>
                {formatWeekRange(dates)}
              </div>
              <div
                className="muted"
                style={{ fontSize: 13, marginTop: 4 }}
              >
                Please submit Billing Tracker by 10am on{' '}
                <span
                  style={{
                    fontWeight: 700,
                    color: 'var(--brand-primary, #E87722)',
                  }}
                >
                  {formatSubmissionDeadline(dates)}
                </span>
              </div>
            </div>
            <StatusBadge status={status} />
          </div>
        )}

        {!crediting && status === STATUS.REJECTED && timesheet?.RejectionReason && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(220, 38, 38, 0.08)',
              color: 'var(--brand-danger)',
              fontSize: 13,
            }}
          >
            <strong>Rejected by {timesheet.RejectedBy || 'Regional Manager'}:</strong>{' '}
            {timesheet.RejectionReason}
          </div>
        )}

        {/* Two-row entry cards — each timesheet entry is its own bordered
            card with the dropdown fields on the top row (wide enough to
            breathe) and the seven day inputs on the bottom row (evenly
            distributed across the full card width). Much easier to fill
            in than the old single-row table, and the card border groups
            the fields that belong to the same plant / row.
            Hidden in adjustment modes (supplement OR credit) — both
            adjustments capture their info via the metadata panel
            below (Reason + Disposition, plus a flat dollar credit
            amount when applicable). Showing four empty entry cards
            on the supplement / credit forms was noise. */}
        {!adjusting && (
        <>
        {/* ---- Grouped category layout ----
            The Billing Tracker used to render one card per "entry", each
            with its own seven-day grid. That was easy to scan with two
            or three entries but quickly turned into a 20-card scroll for
            liaisons covering multiple plants. The grouped layout splits
            the rows into three category cards (Hours / Mileage /
            Expenses) and renders each as a compact table — one row per
            (Plant × Supplier × Coverage Type) on the Hours table, one
            per (Plant × Supplier) on Mileage, and one per receipt on
            Expenses. Daily totals roll up into each table's footer so
            the submitter can immediately see how many hours they put
            against any given day across all suppliers.
            Underneath, the data model is unchanged: every row in
            `rows` still has the same shape, we just bucket it by
            coverageType for rendering. setRow / removeRow / setDayValue
            still take the absolute index into `rows` so handler wiring
            is identical to the old per-card view. */}
        {(() => {
          // Bucket the rows by category, preserving the original index
          // alongside each so handlers keep targeting the correct row.
          // Standard and Extra (Overage) used to share one Hours card
          // with a Type column toggle — they're now split into two
          // dedicated cards so the Type column can go away. Coverage
          // type is implicit in which card the row lives in.
          const standardEntries = [];
          const extraEntries = [];
          const mileageEntries = [];
          const expenseEntries = [];
          rows.forEach((row, idx) => {
            const ct = row.coverageType;
            if (ct === 'Mileage') mileageEntries.push({ row, idx });
            else if (ct === 'Expenses') expenseEntries.push({ row, idx });
            else if (ct === 'Overage') extraEntries.push({ row, idx });
            else standardEntries.push({ row, idx }); // Standard / legacy Other
          });
          // Group every line item by plant so the tracker reads as one
          // block per plant (per Jillian, Aug 2026). Rows with no plant
          // chosen yet fall into an "Unassigned" group shown last. A
          // plant that only carries a Weekly Note (its line items since
          // removed) still surfaces so the note isn't hidden.
          const plantGroupMap = new Map();
          const ensureGroup = (pc) => {
            if (!plantGroupMap.has(pc)) {
              plantGroupMap.set(pc, {
                key: pc,
                standard: [],
                extra: [],
                mileage: [],
                expense: [],
              });
            }
            return plantGroupMap.get(pc);
          };
          standardEntries.forEach((e) =>
            ensureGroup(e.row.plantCode || '').standard.push(e),
          );
          extraEntries.forEach((e) =>
            ensureGroup(e.row.plantCode || '').extra.push(e),
          );
          mileageEntries.forEach((e) =>
            ensureGroup(e.row.plantCode || '').mileage.push(e),
          );
          expenseEntries.forEach((e) =>
            ensureGroup(e.row.plantCode || '').expense.push(e),
          );
          Object.keys(plantNotes || {}).forEach((pc) => {
            if (pc) ensureGroup(pc);
          });
          const plantGroups = [...plantGroupMap.keys()]
            .sort((a, b) => (a === '' ? 1 : b === '' ? -1 : a.localeCompare(b)))
            .map((pc) => {
              const plant = plantByCode.get(pc);
              const name =
                plant?.PlantName || plant?.Title || plant?.Name || '';
              return {
                ...plantGroupMap.get(pc),
                unassigned: !pc,
                label: pc
                  ? name
                    ? `${pc} — ${name}`
                    : pc
                  : 'Unassigned — choose a plant for these rows',
              };
            });
          // Day-of-week totals across every Hours / Mileage row. Used
          // for the footer rows that show submitters how much they
          // billed on a given day across all plants.
          const dayTotal = (entries, key) =>
            entries.reduce(
              (sum, { row }) => sum + (Number(row.values[key]) || 0),
              0,
            );
          // "Billed Hours per Day" — the actual hours from every hour row
          // (Standard + OT alike, 1:1). OT hours are not inflated here; the
          // time-and-a-half premium is applied to the rate at billing time
          // (per Jillian, Aug 2026).
          const billableDayTotal = (entries, key) =>
            entries.reduce(
              (sum, { row }) => sum + (Number(row.values[key]) || 0),
              0,
            );
          // "Commission Hours per Day" — same, but OT (Overage) hours count
          // at 1.5× (Standard stays 1:1). Used for commission calculations
          // where overtime earns time-and-a-half (per Jillian, Aug 2026).
          const commissionDayTotal = (entries, key) =>
            entries.reduce((sum, { row }) => {
              const v = Number(row.values[key]) || 0;
              const mult =
                (row.coverageType || 'Standard') === 'Overage'
                  ? OT_RATE_MULTIPLIER
                  : 1;
              return sum + v * mult;
            }, 0);
          const formatHrs = (n) =>
            n === 0
              ? '0'
              : Number.isInteger(n)
                ? String(n)
                : n.toFixed(2).replace(/\.?0+$/, '');
          // Supplier filter scoped to a single plant — same Active /
          // On Call rule the previous per-entry card used. Keeps the
          // currently-selected supplier in the dropdown even if their
          // status changed so a saved row doesn't silently lose its
          // selection.
          // The Supplier dropdown lists one option per (supplier, SERVICE)
          // at the row's plant (Sep 2026). A supplier with a single row at
          // the plant shows as just its name; one billing several services
          // there (e.g. Nascote: Standard Coverage $35 + Lineside Support
          // $30) shows one option per service, "Nascote — Lineside
          // Support", so the tracker knows which rate and hours apply.
          // The option value is "CODE||service" and is split on change.
          const SVC_SEP = '||';
          const optionValue = (code, service) =>
            `${code || ''}${SVC_SEP}${service || ''}`;
          // Whose tracker this is decides the supplier scope: a Liaison
          // Manager only sees the suppliers they're responsible for at
          // each plant (hard limit, per Jillian, Sep 2026). Directors and
          // Regional Managers submitting their own trackers are unscoped.
          // Evaluated against `me` (the subject), so an RM editing an LM's
          // week sees exactly what that LM would.
          const supplierScope = resolveSupplierScope(me, team);
          const supplierOptionsFor = (row) => {
            const isStandard = (row.coverageType || 'Standard') === 'Standard';
            const plantUpper = String(row.plantCode || '').toUpperCase();
            // A (supplier, service) pair can only appear once under Standard
            // Liaison Hours for a given plant — hide pairs already chosen on
            // OTHER Standard rows at this plant (per Jillian, Aug 2026). OT /
            // Mileage / Expenses rows aren't deduped.
            const pairKey = (code, service) =>
              `${String(code || '').toUpperCase()}|${String(service || '').trim().toLowerCase()}`;
            const rowKey = pairKey(row.supplierCode, row.service);
            const takenAtPlant = isStandard
              ? new Set(
                  rows
                    .filter(
                      (r) =>
                        (r.coverageType || 'Standard') === 'Standard' &&
                        String(r.plantCode || '').toUpperCase() === plantUpper &&
                        r.supplierCode &&
                        pairKey(r.supplierCode, r.service) !== rowKey,
                    )
                    .map((r) => pairKey(r.supplierCode, r.service)),
                )
              : null;
            const out = [];
            for (const s of suppliers) {
              const code = String(s.SupplierCode || '');
              if (!code) continue;
              // Out-of-scope suppliers are never offered. The one exception
              // is a saved row that already points at one (e.g. a week
              // saved before the LM's assignments were set): keep that
              // single choice visible, flagged, so the row can be seen and
              // removed rather than silently submitted.
              const inScope = scopeAllowsSupplierAtPlant(supplierScope, plantUpper, code);
              const isRowSupplierEarly = code === row.supplierCode;
              if (!inScope && !isRowSupplierEarly) continue;
              const scopeFlag = inScope ? '' : ' (not one of your suppliers)';
              const atPlant = assignmentsAtPlant(s, plantUpper);
              const live = atPlant.filter(
                (pa) =>
                  (pa.coverageStatus === 'Active' || pa.coverageStatus === 'On Call') &&
                  // Coverage that ended before this week isn't offered.
                  !coverageEndedOn(pa, dates[0]),
              );
              const isRowSupplier = code === row.supplierCode;
              // Keep the row's saved choice in the list even if that
              // assignment is no longer live, so an old row never loses its
              // selection.
              const candidates = isRowSupplier && live.length === 0 ? atPlant : live;
              const multi = atPlant.length > 1;
              for (const pa of candidates) {
                const service = String(pa.service || '');
                const isRowChoice =
                  isRowSupplier &&
                  String(row.service || '').trim().toLowerCase() ===
                    service.trim().toLowerCase();
                if (
                  !isRowChoice &&
                  takenAtPlant &&
                  takenAtPlant.has(pairKey(code, service))
                ) {
                  continue;
                }
                const svcLabel = multi ? service : serviceLabel(pa);
                out.push({
                  value: optionValue(code, service),
                  supplierCode: code,
                  service,
                  label: (s.Title || code) + (svcLabel ? ` — ${svcLabel}` : '') + scopeFlag,
                });
              }
              // Legacy saved row whose service no longer matches any
              // assignment: still show it so the select isn't blank.
              if (
                isRowSupplier &&
                !out.some((o) => o.supplierCode === code && o.service === String(row.service || ''))
              ) {
                out.push({
                  value: optionValue(code, row.service || ''),
                  supplierCode: code,
                  service: String(row.service || ''),
                  label: (s.Title || code) + scopeFlag,
                });
              }
            }
            // A–Z by the label shown (supplier name, then service) so the
            // list reads like a directory (Oct 2026).
            out.sort((a, b) =>
              String(a.label || '').localeCompare(String(b.label || ''), undefined, {
                sensitivity: 'base',
              }),
            );
            return out;
          };
          // "Paid To" choices for Expense / Mileage rows — the team
          // members assigned to the row's plant (their Team record's
          // plantCodes). The row's saved value stays in the list even
          // if that person is no longer assigned there, so an old row
          // never silently loses its selection (per Jillian, July
          // 2026 — expenses and mileage are reimbursed to a specific
          // person, so billing needs to know who to pay).
          const paidToOptionsFor = (row) => {
            const target = String(row.plantCode || '').toUpperCase().trim();
            const names = (team || [])
              .filter(
                (m) =>
                  target &&
                  (m.plantCodes || []).some(
                    (c) => String(c || '').toUpperCase().trim() === target,
                  ),
              )
              .map((m) => displayFullName(m))
              .filter(Boolean)
              .sort((a, b) => a.localeCompare(b));
            const cur = (row.assignedTo || '').trim();
            if (cur && !names.includes(cur)) names.unshift(cur);
            return names;
          };
          const renderPaidToSelect = ({ row, idx }) => (
            <select
              className="ts-select"
              value={row.assignedTo || ''}
              onChange={(e) => setRow(idx, { assignedTo: e.target.value })}
              disabled={!canEdit || saving || !row.plantCode}
              title={
                !row.plantCode
                  ? 'Pick a Plant first'
                  : 'Team member this row reimburses'
              }
              style={
                row.assignedTo ? undefined : { color: 'var(--brand-muted)' }
              }
            >
              <option value="" style={{ color: 'var(--brand-muted)' }}>
                {row.plantCode ? '— select —' : 'Pick a Plant first'}
              </option>
              {paidToOptionsFor(row).map((n) => (
                <option key={n} value={n} style={{ color: 'var(--brand-ink)' }}>
                  {n}
                </option>
              ))}
            </select>
          );
          // Per-day cell renderer. Shared between Hours and Mileage
          // tables so closed-day shading, future-day locking, and the
          // unit-aware overlay logic only lives in one place.
          const renderDayCell = ({ row, idx }, dayIdx, isMileageCell) => {
            const d = dates[dayIdx];
            const key = DAY_KEYS[dayIdx];
            const plant = plantByCode.get(row.plantCode);
            const closed = isPlantClosedOn(plant, d);
            // Supplier coverage at this plant ended before this day.
            const ended = !isMileageCell && rowCoverageEndedOn(row, suppliers, d);
            const future = isFutureDate(d);
            const rowReady = !!row.plantCode && !!row.supplierCode;
            const disabledCell = !canEdit || saving || future || !rowReady;
            // Under/over-standard cue — Standard hours only. A day
            // billed BELOW the auto-filled standard renders blue,
            // ABOVE renders red; validation then requires an
            // Explanation on the row (per Jillian, July 2026).
            // Weekly-billing suppliers flag the WEEK'S TOTAL, not each
            // day, so suppress the per-day under/over cue for them —
            // the Total cell carries the color instead.
            // On Call rows have no standard to compare against, so they
            // skip the blue/red cue too (Oct 2026).
            const onCallRow = rowIsOnCall(row, suppliers);
            const suppressDayDev =
              onCallRow ||
              ((row.coverageType || 'Standard') === 'Standard' &&
                supplierPlantBillsWeekly(
                  row.plantCode,
                  row.supplierCode,
                  suppliers,
                  row.service,
                  dates,
                ));
            const onCallWorked =
              onCallRow && !future && (Number(row.values[key]) || 0) > 0;
            const dev =
              !isMileageCell && !suppressDayDev
                ? (standardHoursDeviations(row, plants, suppliers, dates) ||
                    {})[key]
                : undefined;
            const devColor = dev
              ? dev.dir === 'under'
                ? STD_UNDER_COLOR
                : STD_OVER_COLOR
              : undefined;
            const cellTitle = !rowReady
              ? 'Pick a Plant and Supplier first'
              : future
                ? "Can't log future hours yet."
                : closed
                  ? 'Plant closed that day'
                  : ended
                    ? dev
                      ? 'Coverage ended — hours here need an Explanation below'
                      : 'Coverage ended'
                    : dev
                      ? `Standard is ${dev.expected} — add an Explanation below`
                      : onCallWorked
                        ? 'On Call — add an Explanation for this day below'
                        : undefined;
            const cellBg = future
              ? 'rgba(100, 116, 139, 0.12)'
              : closed || ended
                ? 'rgba(100, 116, 139, 0.08)'
                : undefined;
            return (
              <TsNumberInput
                // Up/down arrows step hours by a quarter-hour (0.25) and
                // mileage by a whole mile; any decimal can still be typed
                // directly (per Jillian, Sep 2026). The clamp keeps
                // 2-decimal precision so stepping a typed 1.4 goes to 1.65,
                // not a rounded value.
                step={isMileageCell ? 1 : 0.25}
                min={0}
                // Show a literal 0 (not a blank placeholder) when the
                // day is flagged under-standard, so the blue cue lands
                // on the zero the same way it lands on any other number.
                value={row.values[key] || (devColor ? 0 : '')}
                onChange={(raw) => setDayValue(idx, key, raw)}
                disabled={disabledCell}
                placeholder="0"
                // No per-cell unit overlay on either table — the
                // category pill on each card ("Hours" / "Mileage")
                // already labels the whole grid, so repeating
                // "Hours" / "Miles" on every cell is redundant
                // noise. The day cells render as bare numbers,
                // center-aligned to match the centered weekday
                // header above each cell.
                title={cellTitle}
                inputStyle={{
                  background: cellBg,
                  textAlign: 'center',
                  ...(devColor
                    ? { color: devColor, fontWeight: 700 }
                    : null),
                }}
              />
            );
          };
          // Per-day column header — abbreviation + MM/DD. The
          // abbreviation set (M / TU / W / TH / F / SA / SU) is the
          // shorthand the team uses elsewhere in scheduling; the
          // built-in `weekday: 'short'` locale output ("Mon", "Tue")
          // was wider than necessary and stretched the column. Future
          // days render in muted ink.
          const DAY_ABBREV = ['SU', 'M', 'TU', 'W', 'TH', 'F', 'SA'];
          const dayHeader = (d) => {
            const future = isFutureDate(d);
            return (
              <div
                style={{
                  textAlign: 'center',
                  fontSize: 12,
                  fontWeight: 600,
                  color: future
                    ? 'var(--brand-muted, #64748b)'
                    : 'var(--brand-ink)',
                }}
              >
                {DAY_ABBREV[d.getDay()]}{' '}
                {String(d.getMonth() + 1).padStart(2, '0')}/
                {String(d.getDate()).padStart(2, '0')}
              </div>
            );
          };
          // Notes sub-row layout — which optional fields appear is
          // controlled by the `fields` arg so each category can show
          // just what it needs:
          //   • Standard Liaison Hours → ['facility']
          //   • OT Liaison Hours       → ['facility', 'explanation']
          //   • Mileage                → ['explanation']
          //
          // Layout rules:
          //   - Both fields: split the row into 2 / (colSpan-2) cells
          //     so Secondary Facility lines up under Plant+Supplier
          //     and Explanation under Mon-Sun+Total+Actions.
          //   - Facility only: same 2-cell split with the right cell
          //     empty (keeps the box border closed without sprawling
          //     the dropdown across nine columns).
          //   - Explanation only: single full-width cell so the
          //     free-text input gets the entire row.
          //
          // Padding inside each cell is tuned so the inputs line up
          // with the matching dropdowns above (Plant has 8px side
          // padding, Monday day cell has 2px left padding).
          const notesPanel = (
            { row, idx },
            colSpan,
            boxBorder,
            fields = ['facility', 'explanation'],
          ) => {
            const plant = plantByCode.get(row.plantCode);
            const facilityOptions = plant?.sequencingFacilities || [];
            // Explanation is required when the coverage type always
            // demands one (Extra / Mileage), OR when any day on a
            // Standard row differs from the auto-filled standard —
            // matching the blue/red day-cell cue and the submit
            // validation.
            const onCallRow = rowIsOnCall(row, suppliers);
            const rowDevs = explanationDays(row, plants, suppliers, dates);
            const explanationOn =
              EXPLANATION_REQUIRED_COVERAGE_TYPES.has(row.coverageType) ||
              !!rowDevs;
            const rowReady = !!row.plantCode && !!row.supplierCode;
            const showFacility = fields.includes('facility');
            const showExplanation = fields.includes('explanation');
            // Per-day explanations (per Jillian, Sep 2026): a Standard row
            // judged day by day gets ONE box per flagged day, labeled
            // "SU 09/20" etc., instead of a single note for the row.
            // Weekly-billing suppliers are judged on the week's total, so
            // they keep the single box, as do OT / Mileage / Expenses.
            const isWeeklyRow =
              !onCallRow &&
              (row.coverageType || 'Standard') === 'Standard' &&
              supplierPlantBillsWeekly(row.plantCode, row.supplierCode, suppliers, row.service, dates);
            const perDay =
              (row.coverageType || 'Standard') === 'Standard' &&
              !isWeeklyRow &&
              !!rowDevs;
            const flaggedDays = perDay
              ? DAY_KEYS.map((k, i) => ({ key: k, idx: i, dev: rowDevs[k] })).filter(
                  (d) => d.dev,
                )
              : [];
            // A draft saved before per-day notes existed carries one
            // row-level note. Show it in the first flagged day's box so
            // nothing the liaison typed is lost; it moves into
            // dayExplanations the first time any box is edited.
            const legacyNote = String(row.explanation || '').trim();
            const perDayValue = (k) => {
              const stored = row.dayExplanations?.[k];
              if (stored != null && stored !== '') return stored;
              if (legacyNote && flaggedDays.length && flaggedDays[0].key === k) {
                const anyStored = Object.values(row.dayExplanations || {}).some(
                  (t) => String(t || '').trim(),
                );
                if (!anyStored) return legacyNote;
              }
              return '';
            };
            const setDayExplanation = (k, text) => {
              const cur =
                row.dayExplanations && typeof row.dayExplanations === 'object'
                  ? row.dayExplanations
                  : {};
              // Migrate the legacy note into the first flagged day on the
              // first edit, then clear it so it isn't shown twice.
              const seeded = { ...cur };
              if (legacyNote && !Object.values(cur).some((t) => String(t || '').trim())) {
                if (flaggedDays.length) seeded[flaggedDays[0].key] = legacyNote;
              }
              seeded[k] = text;
              setRow(idx, { dayExplanations: seeded, explanation: '' });
            };
            const perDayBoxes = (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--brand-ink)',
                    marginBottom: 2,
                  }}
                >
                  Explanation *{' '}
                  <span className="muted" style={{ fontWeight: 400 }}>
                    {onCallRow
                      ? '(one per On Call day worked)'
                      : "(one per day that's off standard)"}
                  </span>
                </div>
                {flaggedDays.map(({ key, idx: dayIdx, dev }) => {
                  const color =
                    dev.dir === 'oncall'
                      ? ON_CALL_COLOR
                      : dev.dir === 'under'
                        ? STD_UNDER_COLOR
                        : STD_OVER_COLOR;
                  const value = perDayValue(key);
                  return (
                    <div
                      key={key}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '96px 1fr',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ textAlign: 'right', paddingRight: 2, lineHeight: 1.15 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color }}>
                          {dayLabel(dates[dayIdx], key)}
                        </div>
                        <div className="muted" style={{ fontSize: 10 }}>
                          {dev.actual} hr{dev.actual === 1 ? '' : 's'}
                          {dev.dir === 'oncall' ? ', On Call' : `, standard ${dev.expected}`}
                        </div>
                      </div>
                      <input
                        className="ts-input"
                        type="text"
                        value={value}
                        onChange={(e) => setDayExplanation(key, e.target.value)}
                        disabled={!canEdit || !rowReady || saving}
                        placeholder="Required"
                        style={{
                          width: '100%',
                          borderColor: value.trim() ? undefined : color,
                        }}
                      />
                    </div>
                  );
                })}
              </div>
            );

            const facilityCell = (cellColSpan, paddingStyle) => (
              <td
                colSpan={cellColSpan}
                style={{
                  ...paddingStyle,
                  borderLeft: boxBorder,
                  borderBottom: boxBorder,
                }}
              >
                <TsField label="Secondary Facility">
                  <select
                    className="ts-select"
                    value={row.secondaryFacility}
                    onChange={(e) =>
                      setRow(idx, { secondaryFacility: e.target.value })
                    }
                    disabled={!canEdit || !row.plantCode || saving}
                    style={
                      row.secondaryFacility
                        ? undefined
                        : { color: 'var(--brand-muted)' }
                    }
                  >
                    <option
                      value=""
                      style={{ color: 'var(--brand-muted)' }}
                    >
                      Optional
                    </option>
                    {facilityOptions.map((f, fIdx) => (
                      <option
                        key={fIdx}
                        value={f.name || ''}
                        style={{ color: 'var(--brand-ink)' }}
                      >
                        {f.name || `Facility ${fIdx + 1}`}
                      </option>
                    ))}
                  </select>
                </TsField>
              </td>
            );

            const explanationCell = (cellColSpan, paddingStyle) => (
              <td
                colSpan={cellColSpan}
                style={{
                  ...paddingStyle,
                  borderRight: boxBorder,
                  borderBottom: boxBorder,
                }}
              >
                {perDay ? perDayBoxes : (
                <TsField
                  label={`Explanation${explanationOn ? ' *' : ''}`}
                >
                  <input
                    className="ts-input"
                    type="text"
                    value={row.explanation}
                    onChange={(e) =>
                      setRow(idx, { explanation: e.target.value })
                    }
                    disabled={!canEdit || !rowReady || saving}
                    placeholder={
                      !rowReady
                        ? 'Pick a plant & supplier'
                        : explanationOn
                          ? 'Add details'
                          : 'Optional notes'
                    }
                    style={{ width: '100%' }}
                  />
                </TsField>
                )}
              </td>
            );

            // Mobile → stack each field on its own full-width row so the
            // dropdown and explanation get the whole card width instead of
            // being squeezed into the desktop column grid. Rendered inside a
            // single-column wrapper table by HoursTable's mobile branch, so
            // colSpan is irrelevant here.
            if (isMobile) {
              return (
                <>
                  {showFacility && <tr>{facilityCell(1, { padding: '0 2px 10px' })}</tr>}
                  {showExplanation && (
                    <tr>{explanationCell(1, { padding: '0 2px 10px' })}</tr>
                  )}
                </>
              );
            }
            // Both fields → two-cell layout, aligns with column grid above.
            if (showFacility && showExplanation) {
              return (
                <tr>
                  {facilityCell(2, { padding: '0 8px 10px' })}
                  {explanationCell(colSpan - 2, {
                    padding: '0 8px 10px 2px',
                  })}
                </tr>
              );
            }
            // Facility only → keep the 2-cell split with an empty
            // right cell so the box's right border still closes
            // cleanly under Total+Actions.
            if (showFacility) {
              return (
                <tr>
                  {facilityCell(2, { padding: '0 8px 10px' })}
                  <td
                    colSpan={colSpan - 2}
                    style={{
                      padding: '0 8px 10px 2px',
                      borderRight: boxBorder,
                      borderBottom: boxBorder,
                    }}
                  />
                </tr>
              );
            }
            // Explanation only → single full-width cell so the input
            // stretches end to end. Left padding stays 8 to align
            // with the Plant column's leading edge above.
            if (showExplanation) {
              return (
                <tr>
                  <td
                    colSpan={colSpan}
                    style={{
                      padding: '0 8px 10px',
                      borderLeft: boxBorder,
                      borderRight: boxBorder,
                      borderBottom: boxBorder,
                    }}
                  >
                    {perDay ? perDayBoxes : (
                    <TsField
                      label={`Explanation${explanationOn ? ' *' : ''}`}
                    >
                      <input
                        className="ts-input"
                        type="text"
                        value={row.explanation}
                        onChange={(e) =>
                          setRow(idx, { explanation: e.target.value })
                        }
                        disabled={!canEdit || !rowReady || saving}
                        placeholder={
                          !rowReady
                            ? 'Pick a plant & supplier'
                            : explanationOn
                              ? 'Add details'
                              : 'Optional notes'
                        }
                        style={{ width: '100%' }}
                      />
                    </TsField>
                    )}
                  </td>
                </tr>
              );
            }
            // Nothing to show (shouldn't happen in practice).
            return null;
          };
          // Small icon-button used for the per-row Notes toggle and
          // Remove. Square-ish, neutral border, fits inside a table
          // cell without crowding.
          const renderRowAction = (icon, label, onClick, disabled, color) => (
            <button
              type="button"
              onClick={onClick}
              disabled={disabled}
              title={label}
              aria-label={label}
              style={{
                width: 26,
                height: 26,
                padding: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: '#fff',
                border: '1px solid var(--brand-border, #e5e7eb)',
                borderRadius: 4,
                cursor: disabled ? 'not-allowed' : 'pointer',
                color: color || 'var(--brand-ink)',
                fontSize: 13,
                lineHeight: 1,
              }}
            >
              {icon}
            </button>
          );
          // Per-row Plant + Supplier cells — small selects, shared
          // styling between the three tables.
          const renderPlantSelect = ({ row, idx }) => (
            <select
              className="ts-select"
              value={row.plantCode}
              onChange={(e) =>
                setRowWithAutofill(idx, {
                  plantCode: e.target.value,
                  supplierCode: '',
                  secondaryFacility: '',
                })
              }
              disabled={!canEdit || saving}
            >
              <option value="">—</option>
              {/* A–Z by plant code so people can find theirs (Oct 2026). */}
              {[...plantOptions]
                .sort((a, b) =>
                  String(a.PlantCode || '').localeCompare(String(b.PlantCode || ''), undefined, {
                    sensitivity: 'base',
                  }),
                )
                .map((p) => (
                  <option key={p.PlantCode} value={p.PlantCode}>
                    {p.PlantCode}
                  </option>
                ))}
            </select>
          );
          const renderSupplierSelect = ({ row, idx }) => {
            const opts = supplierOptionsFor(row);
            const current = row.supplierCode
              ? optionValue(row.supplierCode, row.service || '')
              : '';
            // Coverage hours on file for this supplier at this plant,
            // shown in gray under the dropdown on Standard rows as a
            // reference while filling in the week (per Jillian, Oct
            // 2026). Format is always "Coverage: <hours> Hours / <Unit>".
            const coverage =
              (row.coverageType || 'Standard') === 'Standard'
                ? coverageCaption(row, suppliers, dates)
                : '';
            return (
              <div style={{ position: 'relative' }}>
                <select
                  className="ts-select"
                  value={current}
                  onChange={(e) => {
                    const [supplierCode = '', service = ''] = e.target.value.split(SVC_SEP);
                    setRowWithAutofill(idx, { supplierCode, service });
                  }}
                  disabled={!canEdit || !row.plantCode || saving}
                >
                  <option value="">
                    {row.plantCode
                      ? opts.length
                        ? '—'
                        : 'No suppliers'
                      : 'Pick a plant'}
                  </option>
                  {opts.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {coverage && (
                  <div
                    style={{
                      // Hangs below the dropdown without changing the
                      // cell's layout height, so the dropdown stays level
                      // with the other boxes in the row; HoursTable gives
                      // caption rows extra vertical padding to make room.
                      position: 'absolute',
                      left: 0,
                      top: '100%',
                      marginTop: 4,
                      color: 'var(--brand-muted)',
                      fontSize: 11.5,
                      whiteSpace: 'nowrap',
                    }}
                    title="Coverage hours set on the Supplier page for this plant"
                  >
                    Coverage:{' '}
                    <span style={{ color: 'var(--brand-ink)', fontWeight: 600 }}>
                      {coverage}
                    </span>
                  </div>
                )}
              </div>
            );
          };
          return (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 16,
                marginTop: 14,
              }}
            >
              {/* A fresh tracker auto-populates with every Active / On-Call
                  supplier at each plant the submitter is assigned to (see
                  the auto-seed effect above), so there's no manual "Add
                  Plant" step. Each plant block still carries its own per-type
                  Add buttons for anything extra — OT, mileage, expenses, or
                  a stray supplier (per Jillian, Aug 2026). */}
              {/* One block per plant. Standard / OT / Mileage / Expenses
                  sub-tables show only that plant's rows; a Weekly Notes
                  box sits at the bottom of each plant's block. */}
              {plantGroups.map((g) => (
                <div
                  key={g.key || '__unassigned'}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: 'var(--brand-ink)',
                      borderBottom: '2px solid var(--brand-primary, #E87722)',
                      paddingBottom: 4,
                    }}
                  >
                    {g.label}
                  </div>
                  {g.standard.length > 0 && (
                    <CategoryCard
                      pill="Standard Liaison Hours"
                      description="Log standard weekly liaison hours. One entry per supplier per plant. Note that any days with hours over or under the standard hours, and every day an On Call supplier works, will be flagged and require an explanation."
                    >
                      <HoursTable
                        entries={g.standard}
                  emptyMessage="No standard hours yet. Click + Add standard row to log time."
                  totalLabel="Total Standard Hours"
                  dates={dates}
                  rows={rows}
                  canEdit={canEdit}
                  saving={saving}
                  renderPlantSelect={renderPlantSelect}
                  renderSupplierSelect={renderSupplierSelect}
                  hasCaption={(row) =>
                    (row.coverageType || 'Standard') === 'Standard' &&
                    !!coverageCaption(row, suppliers, dates)
                  }
                  renderDayCell={renderDayCell}
                  renderRowAction={renderRowAction}
                  removeRow={removeRow}
                  dayHeader={dayHeader}
                  formatRowTotal={formatRowTotal}
                  formatHrs={formatHrs}
                  dayTotal={dayTotal}
                  notesPanel={notesPanel}
                  // Standard hours get both Secondary Facility and
                  // Explanation, but Explanation is optional here
                  // (Standard isn't in EXPLANATION_REQUIRED_COVERAGE_TYPES
                  // so validation never blocks on it). The label
                  // renders without an asterisk in that case.
                  notesFields={['facility', 'explanation']}
                  expandedNotes={expandedNotes}
                  toggleNotes={toggleNotes}
                  rowIsWeekly={rowIsWeekly}
                  rowTotalColor={rowTotalColor}
                />
                    </CategoryCard>
                  )}
                  {/* OT LIAISON HOURS — always require an Explanation and
                      bill at 1.5× the standard rate. */}
                  {g.extra.length > 0 && (
                    <CategoryCard
                      pill="OT Liaison Hours"
                      description="Log overtime hours worked outside standard scheduled coverage. Note that these hours will be charged at 1.5x the standard rate."
                    >
                      <HoursTable
                        entries={g.extra}
                  emptyMessage=""
                  totalLabel="Total OT Hours"
                  dates={dates}
                  rows={rows}
                  canEdit={canEdit}
                  saving={saving}
                  renderPlantSelect={renderPlantSelect}
                  renderSupplierSelect={renderSupplierSelect}
                  hasCaption={(row) =>
                    (row.coverageType || 'Standard') === 'Standard' &&
                    !!coverageCaption(row, suppliers, dates)
                  }
                  renderDayCell={renderDayCell}
                  renderRowAction={renderRowAction}
                  removeRow={removeRow}
                  dayHeader={dayHeader}
                  formatRowTotal={formatRowTotal}
                  formatHrs={formatHrs}
                  dayTotal={dayTotal}
                  notesPanel={notesPanel}
                  // Extra coverage requires an Explanation (validation
                  // enforces it via EXPLANATION_REQUIRED_COVERAGE_TYPES);
                  // Facility is also available since multi-facility
                  // plants may need to specify which one.
                  notesFields={['facility', 'explanation']}
                  expandedNotes={expandedNotes}
                  toggleNotes={toggleNotes}
                  rowIsWeekly={rowIsWeekly}
                  rowTotalColor={rowTotalColor}
                  otBillable
                />
                    </CategoryCard>
                  )}
                  {/* MILEAGE */}
                  {g.mileage.length > 0 && (
                    <CategoryCard
                      pill="Mileage"
                      description="Enter mileage to be billed back. Assign each entry to the team member being reimbursed."
                    >
                {g.mileage.length === 0 ? null : (
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                      // Fixed layout so the column widths below are
                      // honored exactly — the Supplier dropdown stays
                      // narrow (and truncates a long name) instead of
                      // stretching, which frees width for Description.
                      tableLayout: 'fixed',
                    }}
                  >
                    <thead>
                      {/* Mileage columns: Plant, Supplier, Day,
                          Description, Miles, Paid To, Total. Description
                          has no fixed width so it absorbs the leftover
                          space. */}
                      <tr>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            width: 84,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Plant *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            width: 150,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Supplier *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            width: 116,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                          title="Which day of the month this mileage is from"
                        >
                          Day *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Description *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            width: 80,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Miles *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            width: 140,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                          title="Team member this mileage reimburses"
                        >
                          Paid To *
                        </th>
                        <th
                          style={{
                            textAlign: 'right',
                            padding: '4px 6px',
                            width: 68,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Total
                        </th>
                        <th style={{ padding: '4px 6px', width: 34 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {g.mileage.map((entry) => {
                        const { row, idx } = entry;
                        const mRowReady =
                          !!row.plantCode && !!row.supplierCode;
                        // Miles live on a single day. Sum the grid to get
                        // the row's miles.
                        const milesVal = DAY_KEYS.reduce(
                          (acc, k) => acc + (Number(row.values?.[k]) || 0),
                          0,
                        );
                        // The chosen date is a full ISO date so the
                        // submitter can pick any day of the month. Legacy
                        // rows that only have a populated weekday cell get
                        // their date inferred from this week.
                        let mileageDate = row.mileageDate || '';
                        if (!mileageDate) {
                          const nzIdx = DAY_KEYS.findIndex(
                            (k) => (Number(row.values?.[k]) || 0) > 0,
                          );
                          if (nzIdx >= 0 && dates[nzIdx]) {
                            mileageDate = formatLocalDateIso(dates[nzIdx]);
                          }
                        }
                        // Store miles on the weekday cell the chosen date
                        // falls on — the billing engine reads the grid, so
                        // this keeps billing working — and stash the ISO
                        // date in row.mileageDate for display.
                        const setMileage = (iso, miles) => {
                          const values = {
                            mon: 0,
                            tue: 0,
                            wed: 0,
                            thu: 0,
                            fri: 0,
                            sat: 0,
                            sun: 0,
                          };
                          const wd = dayKeyFromIso(iso);
                          if (wd) values[wd] = miles || 0;
                          setRow(idx, {
                            mileageDate: iso,
                            mileageDay: '',
                            values,
                          });
                        };
                        const parseMiles = (raw) => {
                          const n = parseFloat(
                            String(raw).replace(/[^0-9.]/g, ''),
                          );
                          return Number.isFinite(n) ? n : 0;
                        };
                        return (
                          <tr
                            key={idx}
                            style={{
                              borderTop:
                                '1px solid var(--brand-border, #e5e7eb)',
                              background: rowIsWeekly(row)
                                ? WEEKLY_BG
                                : undefined,
                            }}
                          >
                            <td style={{ padding: '6px 6px' }}>
                              {renderPlantSelect(entry)}
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              {renderSupplierSelect(entry)}
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              <select
                                className="ts-select"
                                value={mileageDate}
                                onChange={(e) =>
                                  setMileage(e.target.value, milesVal)
                                }
                                disabled={!canEdit || !mRowReady || saving}
                                style={{ width: '100%' }}
                                title={
                                  mRowReady
                                    ? 'Which day this mileage is from'
                                    : 'Pick a plant & supplier first'
                                }
                              >
                                <option value="">—</option>
                                {monthDays.map((md) => (
                                  <option key={md.iso} value={md.iso}>
                                    {md.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              <input
                                className="ts-input"
                                type="text"
                                value={row.explanation}
                                onChange={(e) =>
                                  setRow(idx, { explanation: e.target.value })
                                }
                                disabled={!canEdit || !mRowReady || saving}
                                placeholder={
                                  mRowReady
                                    ? 'Add details'
                                    : 'Pick a plant & supplier'
                                }
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td
                              style={{ padding: '6px 6px', textAlign: 'right' }}
                            >
                              <input
                                className="ts-input"
                                type="text"
                                inputMode="numeric"
                                value={milesVal ? String(milesVal) : ''}
                                onChange={(e) =>
                                  setMileage(
                                    mileageDate,
                                    parseMiles(e.target.value),
                                  )
                                }
                                disabled={
                                  !canEdit ||
                                  !mRowReady ||
                                  !mileageDate ||
                                  saving
                                }
                                placeholder="0"
                                style={{ textAlign: 'right' }}
                                title={
                                  !mRowReady
                                    ? 'Pick a plant & supplier first'
                                    : !mileageDate
                                      ? 'Pick a day first'
                                      : undefined
                                }
                              />
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              {renderPaidToSelect(entry)}
                            </td>
                            <td
                              style={{
                                padding: '6px 6px',
                                textAlign: 'right',
                                fontWeight: 600,
                              }}
                            >
                              {formatRowTotal(row)}
                            </td>
                            <td style={{ padding: '6px 4px' }}>
                              {canEdit && rows.length > 1
                                ? renderRowAction(
                                    '×',
                                    'Remove this row',
                                    () => removeRow(idx),
                                    saving,
                                    'var(--brand-danger)',
                                  )
                                : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                    </CategoryCard>
                  )}
                  {/* EXPENSES */}
                  {g.expense.length > 0 && (
                    <CategoryCard
                      pill="Expenses"
                      description="Enter billable expenses. Upload one receipt per entry. Assign each entry to the team member being reimbursed."
                    >
                {g.expense.length === 0 ? null : (
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: 13,
                    }}
                  >
                    <thead>
                      {/* Expenses header — same brand-ink 12/600
                          treatment as the Hours / Mileage tables. */}
                      <tr>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            minWidth: 90,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Plant *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            minWidth: 140,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Supplier *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            width: 120,
                            minWidth: 120,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                          title="Which day of the week this expense is from"
                        >
                          Day
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            // Wide minimum so the Description input
                            // gets plenty of horizontal room (this is
                            // the column users write the most into).
                            minWidth: 360,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Description *
                        </th>
                        <th
                          style={{
                            // Header left-aligned to match the
                            // visual left edge of the Amount input
                            // box below it; the value INSIDE the
                            // input stays right-aligned so digits
                            // line up at the decimal point across
                            // rows.
                            textAlign: 'left',
                            padding: '4px 6px',
                            // Trimmed to just fit a dollar value like
                            // "$XXX.XX" — no need for the full 110px
                            // it used to claim.
                            width: 90,
                            minWidth: 90,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                        >
                          Amount *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            minWidth: 130,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                          title="Team member this expense reimburses"
                        >
                          Paid To *
                        </th>
                        <th
                          style={{
                            textAlign: 'left',
                            padding: '4px 6px',
                            minWidth: 160,
                            fontSize: 12,
                            fontWeight: 600,
                            color: 'var(--brand-ink)',
                          }}
                          title="A receipt is required for every expense."
                        >
                          Receipt
                        </th>
                        <th style={{ padding: '4px 6px', width: 40 }} />
                      </tr>
                    </thead>
                    <tbody>
                      {g.expense.map((entry) => {
                        const { row, idx } = entry;
                        const rowReady = !!row.plantCode && !!row.supplierCode;
                        const receiptState = receiptStatus[idx];
                        // Decide whether *this* row's receipt is required
                        // right now (amount + supplier rule). Used to
                        // render a small orange "Required" tag next to
                        // the upload button so the submitter sees the
                        // rule before clicking Submit.
                        const rowSupplier = row.supplierCode
                          ? supplierByCode.get(
                              String(row.supplierCode).toUpperCase(),
                            )
                          : null;
                        const receiptRequired = expenseRowRequiresReceipt(
                          row,
                          rowSupplier,
                        );
                        return (
                          <tr
                            key={idx}
                            style={{
                              borderTop: '1px solid var(--brand-border, #e5e7eb)',
                              background: rowIsWeekly(row)
                                ? WEEKLY_BG
                                : undefined,
                            }}
                          >
                            <td style={{ padding: '6px 6px' }}>
                              {renderPlantSelect(entry)}
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              {renderSupplierSelect(entry)}
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              <select
                                className="ts-select"
                                value={
                                  row.expenseDate ||
                                  (row.expenseDay &&
                                  dates[DAY_KEYS.indexOf(row.expenseDay)]
                                    ? formatLocalDateIso(
                                        dates[DAY_KEYS.indexOf(row.expenseDay)],
                                      )
                                    : '')
                                }
                                onChange={(e) =>
                                  setRow(idx, {
                                    expenseDate: e.target.value,
                                    expenseDay: '',
                                  })
                                }
                                disabled={!canEdit || !rowReady || saving}
                                style={{ width: '100%' }}
                                title={
                                  rowReady
                                    ? 'Which day this expense is from'
                                    : 'Pick a plant & supplier first'
                                }
                              >
                                <option value="">—</option>
                                {monthDays.map((md) => (
                                  <option key={md.iso} value={md.iso}>
                                    {md.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              <input
                                className="ts-input"
                                type="text"
                                value={row.explanation}
                                onChange={(e) =>
                                  setRow(idx, { explanation: e.target.value })
                                }
                                disabled={!canEdit || !rowReady || saving}
                                placeholder={
                                  rowReady
                                    ? 'Add details'
                                    : 'Pick a plant & supplier'
                                }
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td style={{ padding: '6px 6px', textAlign: 'right' }}>
                              <input
                                className="ts-input"
                                type="text"
                                inputMode="decimal"
                                value={
                                  row.expenseAmount == null
                                    ? ''
                                    : String(row.expenseAmount)
                                }
                                // Keep the RAW typed text in state while
                                // editing so the decimal point survives as
                                // it's typed. Parsing to a number on every
                                // keystroke (the old behavior) swallowed the
                                // "." — "12." parsed to 12 before "5" could
                                // be typed, so only whole numbers were
                                // possible. We only sanitize to digits + a
                                // single 2-place decimal here, then round to
                                // cents on blur. Downstream reads coerce with
                                // Number(...) so a partial "12." is harmless.
                                onChange={(e) =>
                                  setRow(idx, {
                                    expenseAmount: sanitizeDollarInput(
                                      e.target.value,
                                    ),
                                  })
                                }
                                onBlur={(e) =>
                                  setRow(idx, {
                                    expenseAmount: parseDollarAmount(
                                      e.target.value,
                                    ),
                                  })
                                }
                                disabled={!canEdit || !rowReady || saving}
                                placeholder="0.00"
                                style={{ textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '6px 6px' }}>
                              {renderPaidToSelect(entry)}
                            </td>
                            <td style={{ padding: '6px 6px', fontSize: 12 }}>
                              {row.receiptFileName ? (
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    flexWrap: 'wrap',
                                  }}
                                >
                                  {row.receiptUrl ? (
                                    <a
                                      href={row.receiptUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: 'var(--brand-primary, #E87722)',
                                      }}
                                    >
                                      {row.receiptFileName}
                                    </a>
                                  ) : (
                                    <span>{row.receiptFileName}</span>
                                  )}
                                  {canEdit && (
                                    <button
                                      type="button"
                                      className="btn btn-outline btn-sm"
                                      onClick={() =>
                                        setRow(idx, {
                                          receiptFileName: '',
                                          receiptUrl: '',
                                        })
                                      }
                                      disabled={saving}
                                      style={{ padding: '2px 8px' }}
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <label
                                  // Styled like a real outline
                                  // button so the upload affordance
                                  // reads as a clickable action
                                  // rather than a piece of muted body
                                  // text. The dotted underline that
                                  // was here before made the label
                                  // look like a hyperlink, which
                                  // didn't match the rest of the
                                  // form's interaction language.
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 6,
                                    cursor:
                                      !canEdit || !rowReady || saving
                                        ? 'not-allowed'
                                        : 'pointer',
                                    color: 'var(--brand-ink)',
                                    background: '#fff',
                                    border:
                                      '1px solid var(--brand-border, #e5e7eb)',
                                    borderRadius: 6,
                                    padding: '4px 12px',
                                    fontSize: 12,
                                    fontWeight: 500,
                                    opacity:
                                      !canEdit || !rowReady || saving
                                        ? 0.6
                                        : 1,
                                  }}
                                >
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png,.heic"
                                    style={{ display: 'none' }}
                                    onChange={(e) => {
                                      const file = e.target.files?.[0];
                                      if (file) onUploadReceipt(idx, file);
                                      e.target.value = '';
                                    }}
                                    disabled={!canEdit || !rowReady || saving}
                                  />
                                  <span>
                                    {receiptState === 'uploading'
                                      ? 'Uploading…'
                                      : 'Upload Receipt'}
                                  </span>
                                </label>
                              )}
                              {/* Per-row required indicator. Only
                                  appears when this exact row needs a
                                  receipt at submit time — a receipt is
                                  required for every expense. Hidden once
                                  a file is attached. */}
                              {receiptRequired && !row.receiptFileName && (
                                <span
                                  style={{
                                    display: 'inline-block',
                                    marginLeft: 6,
                                    fontSize: 11,
                                    fontWeight: 600,
                                    color: 'var(--brand-primary, #E87722)',
                                  }}
                                  title="A receipt is required for every expense."
                                >
                                  Required
                                </span>
                              )}
                              {receiptState === 'error' ? (
                                <div
                                  style={{
                                    color: 'var(--brand-danger)',
                                    fontSize: 11,
                                    marginTop: 4,
                                  }}
                                >
                                  Upload failed — try again.
                                </div>
                              ) : null}
                            </td>
                            <td style={{ padding: '6px 4px' }}>
                              {canEdit && rows.length > 1
                                ? renderRowAction(
                                    '×',
                                    'Remove this expense',
                                    () => removeRow(idx),
                                    saving,
                                    'var(--brand-danger)',
                                  )
                                : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
                    </CategoryCard>
                  )}
                  {/* Total hours per day for this plant — sums every
                      Standard + OT hours row (mileage / expenses aren't
                      hours). Column widths mirror the hours tables so the
                      per-day totals line up under their day columns (per
                      Jillian, Aug 2026). */}
                  {(g.standard.length > 0 || g.extra.length > 0) &&
                    (() => {
                      const hourEntries = [...g.standard, ...g.extra];
                      const grand = DAY_KEYS.reduce(
                        (s, k) => s + billableDayTotal(hourEntries, k),
                        0,
                      );
                      const commissionGrand = DAY_KEYS.reduce(
                        (s, k) => s + commissionDayTotal(hourEntries, k),
                        0,
                      );
                      return (
                        <table
                          style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            fontSize: 13,
                          }}
                        >
                          <colgroup>
                            <col style={{ width: 90 }} />
                            <col style={{ width: 270 }} />
                            {dates.map((_, i) => (
                              <col key={i} style={{ width: 90 }} />
                            ))}
                            <col style={{ width: 60 }} />
                            <col style={{ width: 70 }} />
                          </colgroup>
                          <tbody>
                            <tr
                              style={{
                                borderTop:
                                  '2px solid var(--brand-primary, #E87722)',
                              }}
                            >
                              <td
                                colSpan={2}
                                style={{
                                  textAlign: 'right',
                                  padding: '8px 8px',
                                  fontWeight: 600,
                                  color: 'var(--brand-muted)',
                                }}
                              >
                                Billed Hours per Day
                              </td>
                              {dates.map((d, i) => (
                                <td
                                  key={i}
                                  style={{
                                    textAlign: 'center',
                                    padding: '8px 4px',
                                    fontWeight: 600,
                                    color: 'var(--brand-ink)',
                                  }}
                                >
                                  {formatHrs(
                                    billableDayTotal(hourEntries, DAY_KEYS[i]),
                                  )}
                                </td>
                              ))}
                              <td
                                style={{
                                  textAlign: 'right',
                                  padding: '8px 8px',
                                  fontWeight: 700,
                                  color: 'var(--brand-primary, #E87722)',
                                }}
                              >
                                {formatHrs(grand)}
                              </td>
                              <td />
                            </tr>
                            {/* Commission Hours per Day — OT counts at 1.5×
                                (Standard 1:1), for commission math (per
                                Jillian, Aug 2026). */}
                            <tr>
                              <td
                                colSpan={2}
                                style={{
                                  textAlign: 'right',
                                  padding: '8px 8px',
                                  fontWeight: 600,
                                  color: 'var(--brand-muted)',
                                }}
                              >
                                Commission Hours per Day
                              </td>
                              {dates.map((d, i) => (
                                <td
                                  key={i}
                                  style={{
                                    textAlign: 'center',
                                    padding: '8px 4px',
                                    fontWeight: 600,
                                    color: 'var(--brand-ink)',
                                  }}
                                >
                                  {formatHrs(
                                    commissionDayTotal(hourEntries, DAY_KEYS[i]),
                                  )}
                                </td>
                              ))}
                              <td
                                style={{
                                  textAlign: 'right',
                                  padding: '8px 8px',
                                  fontWeight: 700,
                                  color: 'var(--brand-primary, #E87722)',
                                }}
                              >
                                {formatHrs(commissionGrand)}
                              </td>
                              <td />
                            </tr>
                          </tbody>
                        </table>
                      );
                    })()}
                  {/* Per-plant add buttons — add another entry of a given
                      type straight into this plant, pre-filled with the
                      plant so it lands in the right block (per Jillian,
                      Aug 2026). */}
                  {canEdit && !g.unassigned && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={saving}
                        onClick={() => addRow('Standard', g.key)}
                      >
                        + Add Hours
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={saving}
                        onClick={() => addRow('Overage', g.key)}
                      >
                        + Add OT Hours
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={saving}
                        onClick={() => addRow('Mileage', g.key)}
                      >
                        + Add Mileage
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={saving}
                        onClick={() => addRow('Expenses', g.key)}
                      >
                        + Add Expense
                      </button>
                    </div>
                  )}
                  {/* Written overage-approval uploads — one per (supplier)
                      at this plant that requires approval AND went over
                      standard this week. Blocks submit until attached. */}
                  {!g.unassigned &&
                    (() => {
                      const needs = overageNeeds.filter(
                        (n) => n.plantCode === g.key && n.hasOverage,
                      );
                      if (needs.length === 0) return null;
                      return (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            padding: '10px 12px',
                            border: '1px solid #fdba74',
                            background: '#fff7ed',
                            borderRadius: 8,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 12,
                              fontWeight: 700,
                              color: 'var(--brand-ink)',
                            }}
                          >
                            Written Overage Approval Required
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              color: 'var(--brand-muted)',
                            }}
                          >
                            These suppliers went over standard hours and
                            require written approval attached before you can
                            submit.
                          </div>
                          {needs.map((need) => {
                            const supplierName =
                              supplierByCode.get(
                                String(need.supplierCode)
                                  .trim()
                                  .toUpperCase(),
                              )?.Title || need.supplierCode;
                            const anchor = rows[need.anchorIdx];
                            const has =
                              anchor?.overageApprovalFileName ||
                              anchor?.overageApprovalUrl;
                            const stKey = `${need.plantCode}|${need.supplierCode}`;
                            const st = overageStatus[stKey];
                            return (
                              <div
                                key={stKey}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  flexWrap: 'wrap',
                                  fontSize: 13,
                                }}
                              >
                                <span
                                  style={{ fontWeight: 600, minWidth: 140 }}
                                >
                                  {supplierName}
                                </span>
                                {has ? (
                                  anchor.overageApprovalUrl ? (
                                    <a
                                      href={anchor.overageApprovalUrl}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{
                                        color: 'var(--brand-primary, #E87722)',
                                        fontWeight: 600,
                                      }}
                                    >
                                      {anchor.overageApprovalFileName ||
                                        'Approval file'}
                                    </a>
                                  ) : (
                                    <span>{anchor.overageApprovalFileName}</span>
                                  )
                                ) : (
                                  <span
                                    style={{
                                      color: 'var(--brand-danger, #dc2626)',
                                      fontWeight: 600,
                                    }}
                                  >
                                    Approval required
                                  </span>
                                )}
                                {canEdit && (
                                  <label
                                    className="btn btn-outline btn-sm"
                                    style={{
                                      cursor: saving ? 'default' : 'pointer',
                                    }}
                                  >
                                    {st === 'uploading'
                                      ? 'Uploading…'
                                      : has
                                        ? 'Replace'
                                        : 'Upload Approval'}
                                    <input
                                      type="file"
                                      accept="image/*,application/pdf"
                                      style={{ display: 'none' }}
                                      disabled={saving || st === 'uploading'}
                                      onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) {
                                          onUploadOverageApproval(
                                            need.anchorIdx,
                                            need.plantCode,
                                            need.supplierCode,
                                            f,
                                          );
                                        }
                                        e.target.value = '';
                                      }}
                                    />
                                  </label>
                                )}
                                {typeof st === 'string' &&
                                  st !== 'uploading' && (
                                    <span
                                      style={{
                                        color: 'var(--brand-danger, #dc2626)',
                                        fontSize: 12,
                                      }}
                                    >
                                      {st}
                                    </span>
                                  )}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  {/* Weekly Notes for this plant — one free-text note per
                      plant per week, saved on the timesheet and shown to
                      approvers. Not offered on the Unassigned group. */}
                  {!g.unassigned && (
                    <div>
                      <label
                        style={{
                          display: 'block',
                          fontSize: 12,
                          fontWeight: 600,
                          color: 'var(--brand-ink)',
                          marginBottom: 4,
                        }}
                      >
                        Weekly Notes for Plant{g.key ? ` — ${g.key}` : ''}
                      </label>
                      <textarea
                        className="ts-input"
                        rows={2}
                        value={plantNotes[g.key] || ''}
                        onChange={(e) =>
                          setPlantNotes((prev) => ({
                            ...prev,
                            [g.key]: e.target.value,
                          }))
                        }
                        disabled={!canEdit || saving}
                        placeholder="Add any notes for this plant this week…"
                        style={{ width: '100%', resize: 'vertical' }}
                      />
                    </div>
                  )}
                </div>
              ))}
              {/* Small key at the bottom of the tracker — just the orange
                  swatch + label, shown only when a weekly-billing row is
                  present. */}
              {weeklyLegendItems.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 13,
                    color: 'var(--brand-ink)',
                    marginTop: 4,
                  }}
                >
                  <span
                    style={{
                      width: 14,
                      height: 14,
                      borderRadius: 3,
                      background: '#fed7aa',
                      border: '1px solid #fdba74',
                      display: 'inline-block',
                      flex: '0 0 auto',
                    }}
                  />
                  <span>Suppliers that Bill Weekly</span>
                </div>
              )}
            </div>
          );
        })()}

        {/* Footer row — approval audit footer on the left (shown only
            when the timesheet is Approved), weekly totals stacked on
            the right. Flex row with align-items: flex-end so the
            approval footer's bottom edge lines up with the last
            totals line ("Expenses" when present, otherwise whichever
            totals line is last). */}
        <div
          style={{
            marginTop: 14,
            padding: '12px 14px 4px',
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 20,
            flexWrap: 'wrap',
          }}
        >
          <div
            className="muted"
            style={{
              fontSize: 13,
              flex: '1 1 auto',
              minWidth: 0,
            }}
          >
            {status === STATUS.APPROVED && (
              <>
                Submitted on{' '}
                <strong style={{ fontWeight: 600 }}>
                  {formatApprovalDate(timesheet?.SubmittedAt)}
                </strong>
                . Approved by{' '}
                <strong style={{ fontWeight: 600 }}>
                  {timesheet?.ApprovedBy || 'Regional Manager'}
                </strong>{' '}
                on{' '}
                <strong style={{ fontWeight: 600 }}>
                  {formatApprovalDate(timesheet?.ApprovedAt)}
                </strong>
                .
              </>
            )}
          </div>
          <div
            style={{
              fontSize: 14,
              display: 'flex',
              flexDirection: 'column',
              gap: 6,
              alignItems: 'flex-end',
              marginLeft: 'auto',
            }}
          >
            <TotalLine
              label="Total Billed Hours"
              value={`${totals.totalHours} hrs`}
            />
            <TotalLine
              label="Total Commission Hours"
              value={`${totals.totalCommissionHours} hrs`}
            />
            {totals.totalMiles > 0 && (
              <TotalLine
                label="Total Mileage"
                value={`$${totals.totalMileageDollars.toFixed(2)} (${totals.totalMiles} miles)`}
              />
            )}
            {totals.totalExpenseDollars > 0 && (
              <TotalLine
                label="Expenses"
                value={`$${totals.totalExpenseDollars.toFixed(2)}`}
              />
            )}
          </div>
        </div>
        </>
        )}
        {/* end !crediting wrapper for grid + totals */}

        {!adjusting && canEdit && (
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {/* "+ Add Entry" used to live here; each category card
                now exposes its own typed "+ Add Hours" /
                "+ Add Mileage" / "+ Add Expense" button so the
                row lands in the correct table. The Copy Previous
                Week button is still useful as a one-click to pull
                last week's structure forward — but hidden in
                adjustment modes (supplement / credit) where the
                form is just metadata. */}
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={onCopyPreviousWeek}
              disabled={saving || copyingPrev}
              title="Pull every entry from last week onto this week. Receipts are not copied."
            >
              {copyingPrev ? 'Copying…' : '+ Copy Previous Week'}
            </button>
          </div>
        )}

        {saveError && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 14px',
              borderRadius: 6,
              background: 'rgba(220, 38, 38, 0.08)',
              color: 'var(--brand-danger)',
              fontSize: 13,
              whiteSpace: 'pre-line',
            }}
          >
            {saveError}
          </div>
        )}
        {saveNote && !saveError && (
          <div
            className="muted"
            style={{ marginTop: 12, fontSize: 13 }}
          >
            {saveNote}
          </div>
        )}

        {adjusting && (
          // Adjustment metadata — Reason, billing disposition, and (for
          // credits only) a flat dollar Credit Amount. Same `Section`
          // / `form-grid` / `TextField` treatment every other create
          // form on the site uses (SupplierForm, PartForm, etc.) so
          // this reads as a real form to fill out rather than a
          // muted reference panel. The disposition radios swap based
          // on adjustment type so the user can't accidentally pick a
          // supplemental-invoice disposition on a credit.
          <Section
            title={crediting ? 'Credit Details' : 'Supplement Details'}
            divider={false}
          >
            <div className="form-grid">
              {crediting && (
                <>
                  <TextField
                    label="Credit Amount (USD) *"
                    type="number"
                    step="0.01"
                    min="0"
                    value={creditAmount}
                    onChange={setCreditAmount}
                    placeholder="450.00"
                    required
                    full
                  />
                  <div className="field full">
                    <span className="muted" style={{ fontSize: 11 }}>
                      Flat dollar amount being credited back to
                      supplier. Credits are recorded as a single amount
                      rather than hours.
                    </span>
                  </div>
                </>
              )}
              {supplementing && (
                <>
                  <TextField
                    label="Supplement Amount (USD) *"
                    type="number"
                    step="0.01"
                    min="0"
                    value={supplementAmount}
                    onChange={setSupplementAmount}
                    placeholder="450.00"
                    required
                    full
                  />
                  <div className="field full">
                    <span className="muted" style={{ fontSize: 11 }}>
                      Flat dollar amount to add to the supplier's
                      bill. Supplements are recorded as a single
                      amount rather than hours.
                    </span>
                  </div>
                </>
              )}
              <TextField
                label={
                  crediting
                    ? 'Reason for Credit *'
                    : 'Reason for Supplement *'
                }
                value={supplementReason}
                onChange={setSupplementReason}
                placeholder={
                  crediting
                    ? 'e.g. Credit for extra X hours billed erroneously on Friday X/XX at SHAP'
                    : 'e.g. "Additional overages for launch support at SHAP."'
                }
                maxLength={200}
                required
                full
              />
              <div className="field full">
                <span className="muted" style={{ fontSize: 11 }}>
                  This will appear as a line on invoice — keep it short
                  and specific.
                </span>
              </div>
              <div className="field full">
                <label>
                  {crediting
                    ? 'How should this credit be issued? *'
                    : 'How should this be billed? *'}
                </label>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 6,
                    marginTop: 4,
                  }}
                >
                  {(crediting
                    ? CREDIT_DISPOSITIONS
                    : SUPPLEMENT_DISPOSITIONS
                  ).map((value) => (
                    <label
                      key={value}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        fontSize: 14,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="radio"
                        name="adjustment-disposition"
                        value={value}
                        checked={supplementDisposition === value}
                        onChange={() =>
                          setSupplementDisposition(value)
                        }
                      />
                      {BILLING_DISPOSITION_LABELS[value]}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </Section>
        )}

        {(canEdit || crediting) && (
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
            }}
          >
            <span className="muted" style={{ fontSize: 12 }}>
              * Required fields
            </span>
            <div
              style={{
                display: 'flex',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              {editing ? (
                // RM edit mode — two-action footer. "Save & Keep Pending"
                // persists corrections without approving; "Save & Approve"
                // commits the edited record straight to the Approved
                // section and bounces back to the inbox.
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/timesheets/approvals')}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={onSavePendingFromEdit}
                    disabled={saving}
                  >
                    {saving
                      ? 'Saving…'
                      : retracting
                        ? 'Save & Resubmit for Approval'
                        : 'Save & Keep Pending'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSaveAndApproveFromEdit}
                    disabled={saving}
                  >
                    {saving
                      ? 'Saving…'
                      : retracting
                        ? 'Save & Re-Approve'
                        : 'Save & Approve'}
                  </button>
                </>
              ) : supplementing ? (
                // Supplement mode — single-action footer. The Reason +
                // Billing-Disposition inputs above are required; the
                // handler validates them before creating the new
                // timesheet record.
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/timesheets/approvals')}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSaveSupplement}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Supplement (Pending)'}
                  </button>
                </>
              ) : crediting ? (
                // Credit mode — single-action footer mirroring the
                // supplement flow. Credit-specific inputs (amount,
                // reason, disposition) live in the panel above; the
                // handler validates them before creating the new
                // credit timesheet record.
                <>
                  <button
                    type="button"
                    className="btn btn-outline"
                    onClick={() => navigate('/timesheets/approvals')}
                    disabled={saving}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={onSaveCredit}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Credit (Pending)'}
                  </button>
                </>
              ) : (
                <>
                  <button
                    className="btn btn-outline"
                    onClick={onSaveDraft}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Draft'}
                  </button>
                  {submitterEditable && (
                    <button
                      className="btn btn-primary"
                      onClick={onSubmit}
                      disabled={saving || !canSubmit || !submitWindow.open}
                      title={
                        !submitWindow.open
                          ? `You can submit this week's Billing Tracker on ${submitWindow.opensLabel} (the end of the week). You can keep saving it as a draft until then.`
                          : !canSubmit
                            ? 'Enter at least one row before submitting'
                            : ''
                      }
                    >
                      Submit for Approval
                    </button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
}

// ---- Helpers ----------------------------------------------------------

/**
 * Timesheet-entry form field. Label on top, optional muted hint under it,
 * control below. Lets the two-row card layout keep the same visual
 * rhythm across all five dropdown / text fields without repeating the
 * label markup five times inside each entry.
 */
/**
 * Number input with custom up/down spinner buttons that span the full
 * height of the input. macOS / Safari's native spinners are tiny and
 * hard to click; these replacement buttons are the full cell height so
 * they're an easy click target. Buttons increment/decrement the numeric
 * value by `step`, clamping at `min` (default 0). The inner <input>
 * itself is a plain number input so keyboard entry still works.
 */

/**
 * Card chrome used by each of the three Billing Tracker category sections
 * (Hours / Mileage / Expenses). Stable top-level component (NOT defined
 * inside Timesheets) so React reconciles the table inputs underneath
 * instead of unmounting + remounting the entire subtree on every state
 * change — without this fix, typing a digit into a day cell triggered
 * setRows, which re-rendered Timesheets, which produced a fresh
 * CategoryCard reference, which made React tear down the whole table
 * and wipe the typed value.
 */
/**
 * Chevron icon used by the expand/collapse button on every entry
 * across the Billing Tracker. Inline SVG so the shape renders
 * identically across browsers / fonts. Stroke width matches the
 * visual weight of the × character that sits above it.
 *
 * Defined at module scope so both the Mileage table inline code
 * AND the top-level HoursTable component can reference it without
 * prop-drilling.
 */
function chevronIcon(direction) {
  return (
    <svg
      width="10"
      height="6"
      viewBox="0 0 12 8"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polyline
        points={direction === 'up' ? '2 6 6 2 10 6' : '2 2 6 6 10 2'}
      />
    </svg>
  );
}

function CategoryCard({ pill, description, addLabel, onAdd, children }) {
  return (
    <div
      style={{
        border: '1px solid var(--brand-primary, #E87722)',
        borderRadius: 8,
        padding: '14px 16px',
        background: '#fff',
      }}
    >
      {/* Header row: pill on the left, the "+ Add" button pinned top-right.
          The description ("copy line") sits on its own line below so the
          Add button always stays in the corner instead of wrapping under a
          long description (Jillian, Aug 2026). */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
        }}
      >
        <span
          style={{
            background: 'var(--brand-primary, #E87722)',
            color: '#fff',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: 'uppercase',
            padding: '3px 10px',
            borderRadius: 999,
          }}
        >
          {pill}
        </span>
        {onAdd ? (
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={onAdd}
            style={{ flexShrink: 0 }}
          >
            {addLabel}
          </button>
        ) : null}
      </div>
      {description ? (
        <div
          style={{
            fontSize: 13,
            color: 'var(--brand-muted)',
            marginTop: 8,
            marginBottom: 12,
          }}
        >
          {description}
        </div>
      ) : (
        <div style={{ marginBottom: 12 }} />
      )}
      {children}
    </div>
  );
}

/**
 * Reusable hours-grid table used by both the Standard Liaison Hours
 * and OT Liaison Hours category cards. The body is a row per
 * (Plant × Supplier) with seven day cells + Total + Remove, followed
 * by a second sub-row (notesPanel) holding Secondary Facility +
 * Explanation. Used to be inlined twice — extracted to a stable
 * top-level component so React doesn't unmount the inputs on every
 * keystroke (the same fix as CategoryCard).
 *
 * Props are mostly closures captured by the parent Timesheets
 * component (renderPlantSelect, renderDayCell, etc.). They're
 * re-created every render but passed through here as plain props, so
 * HoursTable re-renders cleanly without remounting its children.
 */
function HoursTable({
  entries,
  emptyMessage,
  totalLabel,
  dates,
  rows,
  canEdit,
  saving,
  renderPlantSelect,
  renderSupplierSelect,
  // (row) => true when the supplier cell shows the gray "Coverage: …"
  // caption under its dropdown. Such rows get taller, symmetric padding
  // so the caption has room while every box stays vertically centered
  // and level with its neighbours (per Jillian, Oct 2026).
  hasCaption,
  renderDayCell,
  renderRowAction,
  removeRow,
  dayHeader,
  formatRowTotal,
  formatHrs,
  dayTotal,
  notesPanel,
  notesFields, // which fields the chevron-expanded panel shows
  expandedNotes,
  toggleNotes,
  // Weekly-billing helpers (optional). rowIsWeekly(row) → light-orange
  // row wash; rowTotalColor(row) → color for the Total cell when the
  // week's total is off standard.
  rowIsWeekly,
  rowTotalColor,
  // Retained for call-site compatibility; OT hours are no longer inflated
  // (the 1.5× premium moved to the rate at billing time, per Jillian, Aug
  // 2026), so footer totals now show actual hours regardless of this flag.
  otBillable,
}) {
  const isMobile = useIsMobile();
  // Footer totals show the actual hours entered — no 1.5× on OT hours.
  const billAdj = (n) => n;
  const weeklyBgFor = (row) =>
    rowIsWeekly && rowIsWeekly(row) ? WEEKLY_BG : undefined;
  const totalColorFor = (row) => (rowTotalColor ? rowTotalColor(row) : undefined);
  if (entries.length === 0) {
    // Sections that don't want any empty-state copy pass an empty
    // string for `emptyMessage`; render nothing in that case so the
    // card's pill + Add button stand alone.
    if (!emptyMessage) return null;
    return (
      <p className="muted" style={{ margin: 0, fontSize: 13 }}>
        {emptyMessage}
      </p>
    );
  }
  // ---- Mobile layout --------------------------------------------------
  // The desktop grid (Plant + Supplier + 7 day columns + Total + Actions)
  // is far too wide for a phone, so each entry stacks into a card: Plant
  // and Supplier full-width, the seven day inputs in a wrapping 4-up grid
  // (each labeled with its weekday), then the row total and actions. The
  // optional Secondary Facility / Explanation panel stacks full-width
  // below via the notesPanel's own mobile branch.
  if (isMobile) {
    const boxBorder = '1px solid var(--brand-border, #e5e7eb)';
    const labelStyle = {
      display: 'block',
      fontSize: 12,
      fontWeight: 600,
      color: 'var(--brand-ink)',
      marginBottom: 4,
    };
    const grandTotal = billAdj(
      DAY_KEYS.reduce((s, k) => s + dayTotal(entries, k), 0),
    );
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16, fontSize: 13 }}>
        {entries.map((entry) => {
          const { row, idx } = entry;
          const expanded = !!(expandedNotes && expandedNotes.has(idx));
          return (
            <div
              key={idx}
              style={{
                border: boxBorder,
                borderRadius: 8,
                padding: 12,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                background: weeklyBgFor(row),
              }}
            >
              <div>
                <span style={labelStyle}>Plant *</span>
                {renderPlantSelect(entry)}
              </div>
              <div
                // Room for the absolutely-positioned "Coverage: …"
                // caption under the dropdown on the mobile card.
                style={hasCaption && hasCaption(row) ? { paddingBottom: 20 } : undefined}
              >
                <span style={labelStyle}>Supplier *</span>
                {renderSupplierSelect(entry)}
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                  gap: 8,
                }}
              >
                {dates.map((d, dIdx) => (
                  <div
                    key={dIdx}
                    style={{ display: 'flex', flexDirection: 'column', gap: 4 }}
                  >
                    {dayHeader(d)}
                    {renderDayCell(entry, dIdx, false)}
                  </div>
                ))}
              </div>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  borderTop: boxBorder,
                  paddingTop: 8,
                }}
              >
                <span style={{ fontWeight: 600, color: 'var(--brand-muted)' }}>
                  Weekly total
                </span>
                <span
                  style={{
                    fontWeight: 700,
                    ...(totalColorFor(row) ? { color: totalColorFor(row) } : null),
                  }}
                >
                  {formatRowTotal(row)}
                </span>
              </div>
              <div
                style={{
                  display: 'flex',
                  gap: 8,
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                }}
              >
                {canEdit
                  ? renderRowAction(
                      chevronIcon(expanded ? 'up' : 'down'),
                      expanded
                        ? 'Hide Secondary Facility / Explanation'
                        : 'Add Secondary Facility / Explanation',
                      () => toggleNotes(idx),
                      saving,
                      'var(--brand-ink)',
                    )
                  : null}
                {canEdit && rows.length > 1
                  ? renderRowAction(
                      '×',
                      'Remove this row',
                      () => removeRow(idx),
                      saving,
                      'var(--brand-danger)',
                    )
                  : null}
              </div>
              {expanded ? (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <tbody>{notesPanel(entry, 1, undefined, notesFields)}</tbody>
                </table>
              ) : null}
            </div>
          );
        })}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: 10,
            borderTop: boxBorder,
          }}
        >
          <span style={{ fontWeight: 600, color: 'var(--brand-muted)' }}>
            {totalLabel}
          </span>
          <span style={{ fontWeight: 700, color: 'var(--brand-primary, #E87722)' }}>
            {formatHrs(grandTotal)}
          </span>
        </div>
      </div>
    );
  }

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: 13,
      }}
    >
      <thead>
        {/* Header styling: brand-ink 12/600 across the board — matches
            the dayHeader styling inside the day <th>s so labels read
            uniformly without the column headers feeling like a
            different family from the day labels. */}
        <tr>
          <th
            style={{
              textAlign: 'left',
              padding: '4px 6px',
              width: 90,
              minWidth: 90,
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--brand-ink)',
            }}
          >
            Plant *
          </th>
          <th
            style={{
              textAlign: 'left',
              padding: '4px 6px',
              width: 270,
              minWidth: 270,
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--brand-ink)',
            }}
          >
            Supplier *
          </th>
          {dates.map((d, dIdx) => (
            <th
              key={dIdx}
              style={{ padding: '4px 4px', width: 90, minWidth: 90 }}
            >
              {dayHeader(d)}
            </th>
          ))}
          <th
            style={{
              textAlign: 'right',
              padding: '4px 6px',
              width: 60,
              fontSize: 12,
              fontWeight: 600,
              color: 'var(--brand-ink)',
            }}
          >
            Total
          </th>
          <th style={{ padding: '4px 6px', width: 70 }} />
        </tr>
      </thead>
      <tbody>
        {entries.map((entry, entryPos) => {
          const { row, idx } = entry;
          // 2 leading cols (Plant + Supplier) + 7 day cells + Total + Actions
          const colSpan = 2 + dates.length + 2;
          // Each entry renders as a "box" — borders applied to the
          // appropriate cells (main row gets top/left/right, notes-
          // panel gets bottom/left/right) so the two sub-rows visually
          // read as a single bounded unit. A transparent 8px spacer
          // row between entries keeps consecutive boxes from merging
          // under `border-collapse: collapse`.
          const boxBorder = '1px solid var(--brand-border, #e5e7eb)';
          const isLast = entryPos === entries.length - 1;
          // When collapsed (notes hidden), the main row's bottom
          // border closes the box by itself. When expanded, the
          // notesPanel <td>s carry the bottom border and the main
          // row's bottom border stays off so the two sub-rows read
          // as a single continuous bounded unit.
          const expanded = !!(expandedNotes && expandedNotes.has(idx));
          const mainBottom = expanded ? undefined : boxBorder;
          const weeklyBg = weeklyBgFor(row);
          const totalColor = totalColorFor(row);
          // Rows with a caption under the Supplier box get 22px of
          // padding top and bottom (instead of 8px): the caption needs
          // ~20px below the dropdown, and keeping the padding symmetric
          // keeps every box centered on the same line.
          const padY = hasCaption && hasCaption(row) ? 22 : 8;
          return (
            <Fragment key={idx}>
              <tr style={weeklyBg ? { background: weeklyBg } : undefined}>
                <td
                  style={{
                    padding: `${padY}px 8px`,
                    borderTop: boxBorder,
                    borderLeft: boxBorder,
                    borderBottom: mainBottom,
                  }}
                >
                  {renderPlantSelect(entry)}
                </td>
                <td
                  style={{
                    padding: `${padY}px 8px`,
                    borderTop: boxBorder,
                    borderBottom: mainBottom,
                  }}
                >
                  {renderSupplierSelect(entry)}
                </td>
                {dates.map((d, dIdx) => (
                  <td
                    key={dIdx}
                    style={{
                      padding: `${padY}px 2px`,
                      borderTop: boxBorder,
                      borderBottom: mainBottom,
                    }}
                  >
                    {renderDayCell(entry, dIdx, false)}
                  </td>
                ))}
                <td
                  style={{
                    textAlign: 'right',
                    padding: `${padY}px 8px`,
                    fontWeight: totalColor ? 700 : 600,
                    borderTop: boxBorder,
                    borderBottom: mainBottom,
                    ...(totalColor ? { color: totalColor } : null),
                  }}
                  title={
                    totalColor
                      ? 'Weekly total is off standard — add an Explanation below'
                      : undefined
                  }
                >
                  {formatRowTotal(row)}
                </td>
                <td
                  style={{
                    padding: `${padY}px 6px`,
                    borderTop: boxBorder,
                    borderRight: boxBorder,
                    borderBottom: mainBottom,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      alignItems: 'flex-end',
                    }}
                  >
                    {canEdit && rows.length > 1
                      ? renderRowAction(
                          '×',
                          'Remove this row',
                          () => removeRow(idx),
                          saving,
                          'var(--brand-danger)',
                        )
                      : null}
                    {canEdit
                      ? renderRowAction(
                          chevronIcon(expanded ? 'up' : 'down'),
                          expanded
                            ? 'Hide Secondary Facility / Explanation'
                            : 'Add Secondary Facility / Explanation',
                          () => toggleNotes(idx),
                          saving,
                          'var(--brand-ink)',
                        )
                      : null}
                  </div>
                </td>
              </tr>
              {expanded
                ? notesPanel(entry, colSpan, boxBorder, notesFields)
                : null}
              {!isLast ? (
                <tr aria-hidden="true">
                  <td colSpan={colSpan} style={{ height: 8, padding: 0 }} />
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </tbody>
      <tfoot>
        <tr
          style={{
            borderTop: '1px solid var(--brand-border, #e5e7eb)',
            color: 'var(--brand-muted)',
          }}
        >
          <td
            colSpan={2}
            style={{
              textAlign: 'right',
              padding: '8px 6px',
              fontWeight: 600,
            }}
          >
            {totalLabel}
          </td>
          {DAY_KEYS.map((k) => (
            <td
              key={k}
              style={{
                textAlign: 'center',
                padding: '8px 4px',
                fontWeight: 600,
                color: 'var(--brand-ink)',
              }}
            >
              {formatHrs(billAdj(dayTotal(entries, k)))}
            </td>
          ))}
          <td
            style={{
              textAlign: 'right',
              padding: '8px 6px',
              fontWeight: 700,
              color: 'var(--brand-primary, #E87722)',
            }}
          >
            {formatHrs(
              billAdj(DAY_KEYS.reduce((s, k) => s + dayTotal(entries, k), 0)),
            )}
          </td>
          <td />
        </tr>
      </tfoot>
    </table>
  );
}

function TsNumberInput({
  value,
  onChange,
  step = 1,
  min = 0,
  max,
  placeholder,
  disabled,
  title,
  inputStyle,
  // Unit suffix that stays visible alongside the typed number ("Miles"
  // / "Hours"). Without this the placeholder ("0 Miles") was the only
  // unit indicator and it disappeared as soon as the submitter typed
  // a real number. Rendered as a muted overlay between the input
  // text and the spinner arrows.
  unit,
}) {
  const numeric = Number(value) || 0;
  const clamp = (n) => {
    if (Number.isFinite(min) && n < min) return min;
    if (Number.isFinite(max) && n > max) return max;
    // Always keep 2 decimals so a whole-unit step preserves any decimal
    // the user typed (1.4 + 1 → 2.4, not 2), while stripping 0.1 + 0.2
    // style float drift (per Jillian, Aug 2026).
    return Math.round(n * 100) / 100;
  };
  const bump = (direction) => {
    if (disabled) return;
    onChange(String(clamp(numeric + direction * step)));
  };
  // Reserve extra horizontal space on the right side of the input for
  // the unit overlay when one is set. Default 28px (spinner arrows
  // only) → 90px (spinner arrows + unit text region + breathing
  // room between the number and the unit so "2 Hours" doesn't read
  // as "2Hours").
  const padRight = unit ? 90 : undefined;
  return (
    <div className="ts-number">
      <input
        className="ts-input"
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={placeholder}
        title={title}
        // Background tint (closed / future day) lives on the input
        // itself, not the wrapper, so the spinner-button strip stays
        // its own crisp gray regardless of the cell state.
        style={{
          textAlign: 'right',
          ...(padRight ? { paddingRight: padRight } : null),
          ...(inputStyle || {}),
        }}
      />
      {unit ? (
        <span
          // Pointer-events off so clicks fall through to the input
          // (Tab/click into the field still works as if the overlay
          // weren't there). Positioned between the input text and
          // the spinner buttons. Font-size, weight, family, and
          // color all deliberately match .ts-input so the unit reads
          // as a natural continuation of the typed number ("4 Miles"
          // looks like one phrase, not value + label). The fixed
          // 56-px-wide region (left-aligned with paddingLeft: 8)
          // means "Hours" and "Miles" both start at the same x, and
          // the input's 90-px right padding leaves a comfortable gap
          // between the right edge of the number and the start of
          // the unit text.
          style={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 28,
            width: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            paddingLeft: 8,
            fontSize: 14,
            fontWeight: 400,
            fontFamily: 'inherit',
            color: 'var(--brand-ink)',
            pointerEvents: 'none',
          }}
        >
          {unit}
        </span>
      ) : null}
      <div className="ts-number-arrows">
        <button
          type="button"
          className="ts-number-arrow"
          onClick={() => bump(+1)}
          disabled={disabled}
          tabIndex={-1}
          aria-label="Increase"
        >
          ▲
        </button>
        <button
          type="button"
          className="ts-number-arrow"
          onClick={() => bump(-1)}
          disabled={disabled || numeric <= min}
          tabIndex={-1}
          aria-label="Decrease"
        >
          ▼
        </button>
      </div>
    </div>
  );
}

/**
 * One line of the weekly totals strip. Label stays in the default ink
 * color; the value is bold and Proxemics orange so all three roll-up
 * lines (Hours / Mileage / Expenses) share the same headline treatment.
 */
function TotalLine({ label, value }) {
  return (
    <div>
      <span style={{ marginRight: 6 }}>{label}:</span>
      <span
        style={{
          fontWeight: 700,
          color: 'var(--brand-primary, #E87722)',
        }}
      >
        {value}
      </span>
    </div>
  );
}

function TsField({ label, hint, children }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--brand-ink)',
          marginBottom: 2,
        }}
      >
        {label}
      </div>
      {hint && (
        <div
          className="muted"
          style={{ fontSize: 10, marginBottom: 4, lineHeight: 1.2 }}
        >
          {hint}
        </div>
      )}
      {children}
    </div>
  );
}

// Keep the exact hours the submitter entered — no 15-minute (0.25)
// snapping. We round to 2 decimals only to guard against floating-point
// drift (e.g. 0.1 + 0.2), so any value like 1.4 or 2.35 is preserved as
// typed (per Jillian, Aug 2026). Blank / non-numeric / negative → 0 so
// the totals row never breaks.
function parseHoursExact(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

// Dollar amounts (Expenses rows) round to cents rather than quarters.
// Blank / non-numeric / negative → 0 so the weekly totals stay sane.
function parseDollarAmount(raw) {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

// Sanitize a dollar amount AS IT'S TYPED — keep digits and a single
// decimal point (max 2 fractional digits) and return the raw string so
// the field can hold an in-progress value like "12." or "12.5" without
// the decimal point being stripped mid-entry. Final rounding to cents
// happens on blur via parseDollarAmount.
function sanitizeDollarInput(raw) {
  let v = String(raw == null ? '' : raw).replace(/[^0-9.]/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot >= 0) {
    const intPart = v.slice(0, firstDot);
    // Drop any further dots and cap the fraction at 2 digits.
    const fracPart = v.slice(firstDot + 1).replace(/\./g, '').slice(0, 2);
    v = `${intPart}.${fracPart}`;
  }
  return v;
}

// Render a row's total with the right unit suffix:
//   Mileage   → dollars at the IRS rate
//   Expenses  → dollars (the amount entered directly)
//   other     → hours (bare number)
function formatRowTotal(row) {
  const t = rowTotal(row);
  if (row.coverageType === 'Mileage') {
    return `$${calcMileageDollars(t).toFixed(2)}`;
  }
  if (row.coverageType === 'Expenses') {
    return `$${t.toFixed(2)}`;
  }
  // OT hours are shown as the ACTUAL hours worked — no 1.5× applied to the
  // hours. The time-and-a-half premium is applied to the RATE at billing /
  // invoicing time instead (per Jillian, Aug 2026).
  return t.toString();
}

// A row counts as "non-empty" — and therefore submittable — when it has a
// plant selected AND some actual value. Expenses rows just need a dollar
// amount; everything else needs at least one day value above zero. Blank
// placeholder rows left in the grid don't block submission.
function rowIsNonEmpty(row) {
  if (!row.plantCode) return false;
  if (row.coverageType === 'Expenses') {
    return (Number(row.expenseAmount) || 0) > 0;
  }
  return DAY_KEYS.some((k) => (Number(row.values[k]) || 0) > 0);
}

// Day-cell colors for Standard hours that deviate from the auto-filled
// standard (per Jillian, July 2026): billed UNDER standard → blue,
// billed OVER standard → red. Either way the row needs an Explanation.
export const STD_UNDER_COLOR = '#2563eb';
export const STD_OVER_COLOR = '#dc2626';

// Light-orange row wash for suppliers that bill by the WEEK (Coverage
// Unit = "Per Week" on their plant assignment). These rows are flagged
// across the Billing Tracker so a reviewer can tell at a glance which
// suppliers are billed as a weekly lump rather than per shift/day, and
// a matching "Suppliers that Bill Weekly" key explains the color (per
// Jillian, Aug 2026).
const WEEKLY_BG = '#fff7ed';

/**
 * Per-day deviation of a Standard hours row from its auto-filled
 * standard (computeStandardHoursByDay). Returns null when the row isn't
 * a Standard row, has no plant/supplier picked, or no standard
 * allocation exists — otherwise an object keyed by DAY_KEYS holding
 * { expected, actual, dir: 'under' | 'over' } for JUST the days that
 * differ. Skips future days (not logged yet) and plant-closure days
 * (the closure is its own explanation). Expected values are rounded to
 * 2 decimals exactly like the auto-fill, so an untouched auto-filled
 * row never reports a deviation.
 */
export function standardHoursDeviations(row, plants, suppliers, dates) {
  const ct = row?.coverageType || 'Standard';
  if (ct === 'Mileage' || ct === 'Expenses' || ct === 'Overage') return null;
  if (!row.plantCode || !row.supplierCode) return null;
  const byDay = computeStandardHoursByDay(
    row.plantCode,
    row.supplierCode,
    plants,
    suppliers,
    row.service,
    dates,
  );
  if (!byDay) return null;
  const plant = (Array.isArray(plants) ? plants : []).find(
    (p) => (p.PlantCode || '') === row.plantCode,
  );
  const out = {};
  for (let i = 0; i < DAY_KEYS.length; i += 1) {
    const k = DAY_KEYS[i];
    const date = (dates || [])[i];
    if (!date || isFutureDate(date)) continue;
    if (isPlantClosedOn(plant, date)) continue;
    // After the coverage End Date the standard is 0, so any hours typed
    // there read as over-standard and need an Explanation (Sep 2026).
    const ended = rowCoverageEndedOn(row, suppliers, date);
    const expected = ended ? 0 : Math.round((Number(byDay[k]) || 0) * 100) / 100;
    const actual = Number(row.values?.[k]) || 0;
    if (actual === expected) continue;
    out[k] = { expected, actual, dir: actual < expected ? 'under' : 'over' };
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Is this row billed OVER its standard coverage (the red cue)? Standard
 * rows only. Weekly-billing suppliers are judged on the week's total;
 * everyone else on any single day above standard. Used by the Approvals
 * "Overages Only" filter (Nov 2026).
 */
export function rowIsOverStandard(row, plants, suppliers, dates) {
  if ((row?.coverageType || 'Standard') !== 'Standard') return false;
  if (!row?.plantCode || !row?.supplierCode) return false;
  if (supplierPlantBillsWeekly(row.plantCode, row.supplierCode, suppliers, row.service, dates)) {
    return weeklyStandardDeviation(row, plants, suppliers, dates)?.dir === 'over';
  }
  const devs = standardHoursDeviations(row, plants, suppliers, dates);
  return !!devs && Object.values(devs).some((d) => d.dir === 'over');
}

/**
 * Does this supplier bill by the WEEK at this plant? True when the
 * supplier's plant assignment carries a "Per Week" Coverage Unit
 * (hoursUnit). Weekly-billing rows get the light-orange highlight and
 * flag their over/under-standard cue on the WEEKLY TOTAL rather than on
 * individual day cells.
 */
function supplierPlantBillsWeekly(plantCode, supplierCode, suppliers, service, dates) {
  if (!plantCode || !supplierCode) return false;
  const supplier = (suppliers || []).find(
    (s) => (s.SupplierCode || '') === supplierCode,
  );
  if (!supplier) return false;
  // `service` picks between several rows at the same plant (Sep 2026).
  const pa = findAssignment(supplier, plantCode, service);
  if (!pa) return false;
  // Coverage can change over time (Oct 2026): the unit in effect on the
  // week's Monday decides, matching the Monday weekly lump.
  const monday = Array.isArray(dates) ? dates[DAY_KEYS.indexOf('mon')] : null;
  const cov = coverageOn(pa, monday || new Date());
  return String(cov?.hoursUnit || '').trim().toLowerCase() === 'per week';
}

/**
 * Weekly-total deviation for a "Per Week" supplier's Standard hours
 * row. Instead of flagging individual days (a weekly-billed supplier
 * can spread the week's hours however they like), we compare the SUM of
 * the week's billed hours against the SUM of the standard allocation
 * and flag only the total. Future days and plant-closure days are
 * excluded from BOTH sides so a partially-entered week doesn't read as
 * "under." Returns { expected, actual, dir: 'under'|'over' } when they
 * differ, else null (on-target → the total renders in the default
 * black).
 */
export function weeklyStandardDeviation(row, plants, suppliers, dates) {
  if ((row?.coverageType || 'Standard') !== 'Standard') return null;
  if (!row.plantCode || !row.supplierCode) return null;
  if (!supplierPlantBillsWeekly(row.plantCode, row.supplierCode, suppliers, row.service, dates)) {
    return null;
  }
  const byDay = computeStandardHoursByDay(
    row.plantCode,
    row.supplierCode,
    plants,
    suppliers,
    row.service,
    dates,
  );
  if (!byDay) return null;
  const plant = (Array.isArray(plants) ? plants : []).find(
    (p) => (p.PlantCode || '') === row.plantCode,
  );
  let expected = 0;
  let actual = 0;
  for (let i = 0; i < DAY_KEYS.length; i += 1) {
    const k = DAY_KEYS[i];
    const date = (dates || [])[i];
    if (!date || isFutureDate(date)) continue;
    if (isPlantClosedOn(plant, date)) continue;
    // Days after the coverage End Date expect 0 but still count actuals.
    if (!rowCoverageEndedOn(row, suppliers, date)) expected += Number(byDay[k]) || 0;
    actual += Number(row.values?.[k]) || 0;
  }
  expected = Math.round(expected * 100) / 100;
  actual = Math.round(actual * 100) / 100;
  if (actual === expected) return null;
  return { expected, actual, dir: actual < expected ? 'under' : 'over' };
}

/**
 * Written-overage-approval needs across the whole tracker. For every
 * (plant, supplier) whose supplier record has RequireOverageApproval ===
 * 'Yes', returns { plantCode, supplierCode, anchorIdx, hasOverage,
 * hasFile }:
 *   • hasOverage — the pair went OVER standard this week: a Standard
 *     day (or weekly total, for weekly-billing suppliers) above standard,
 *     OR an OT Hours (Overage) row carrying any hours.
 *   • anchorIdx — the pair's Standard row index (falls back to the first
 *     labor row) that carries the single approval file for the week.
 *   • hasFile — whether an approval file is attached on the anchor row.
 * Used by both the Billing Tracker's upload UI and the submit-time
 * validation (hard block when hasOverage && !hasFile).
 */
export function overageApprovalNeeds(
  rows,
  suppliers = [],
  plants = [],
  dates = [],
) {
  const supplierByCode = new Map(
    (suppliers || []).map((s) => [
      String(s.SupplierCode || '').trim().toUpperCase(),
      s,
    ]),
  );
  const pairs = new Map();
  (rows || []).forEach((row, idx) => {
    const ct = row?.coverageType || 'Standard';
    if (ct === 'Mileage' || ct === 'Expenses') return; // labor rows only
    const pc = String(row.plantCode || '');
    const sc = String(row.supplierCode || '');
    if (!pc || !sc) return;
    const key = `${pc}|${sc}`;
    if (!pairs.has(key)) {
      pairs.set(key, {
        plantCode: pc,
        supplierCode: sc,
        rowIdxs: [],
        stdIdx: -1,
        hasFile: false,
      });
    }
    const p = pairs.get(key);
    p.rowIdxs.push(idx);
    if ((ct === 'Standard' || ct === 'Other') && p.stdIdx < 0) p.stdIdx = idx;
    if (row.overageApprovalFileName || row.overageApprovalUrl) p.hasFile = true;
  });
  const out = [];
  for (const p of pairs.values()) {
    const supplier = supplierByCode.get(p.supplierCode.trim().toUpperCase());
    if ((supplier?.RequireOverageApproval || '').trim() !== 'Yes') continue;
    let hasOverage = false;
    for (const idx of p.rowIdxs) {
      const row = rows[idx];
      const ct = row?.coverageType || 'Standard';
      if (ct === 'Overage') {
        const sum = DAY_KEYS.reduce(
          (s, k) => s + (Number(row.values?.[k]) || 0),
          0,
        );
        if (sum > 0) {
          hasOverage = true;
          break;
        }
      } else if (supplierPlantBillsWeekly(p.plantCode, p.supplierCode, suppliers, row.service, dates)) {
        const wd = weeklyStandardDeviation(row, plants, suppliers, dates);
        if (wd && wd.dir === 'over') {
          hasOverage = true;
          break;
        }
      } else {
        const devs = standardHoursDeviations(row, plants, suppliers, dates);
        if (devs && Object.values(devs).some((d) => d.dir === 'over')) {
          hasOverage = true;
          break;
        }
      }
    }
    const anchorIdx = p.stdIdx >= 0 ? p.stdIdx : p.rowIdxs[0];
    out.push({
      plantCode: p.plantCode,
      supplierCode: p.supplierCode,
      anchorIdx,
      hasOverage,
      hasFile: p.hasFile,
    });
  }
  return out;
}

// Teal used for On Call cues — matches the On Call status pill.
export const ON_CALL_COLOR = '#0d9488';

/**
 * Is this a Standard hours row for a supplier whose Coverage Status at
 * the row's plant (and service) is "On Call"? On Call suppliers have no
 * standing hours, so every day they bill needs its own Explanation
 * instead of the over/under-standard check (per Jillian, Oct 2026).
 */
export function rowIsOnCall(row, suppliers) {
  if ((row?.coverageType || 'Standard') !== 'Standard') return false;
  if (!row.plantCode || !row.supplierCode) return false;
  const supplier = (suppliers || []).find(
    (s) => (s.SupplierCode || '') === row.supplierCode,
  );
  if (!supplier) return false;
  const pa = findAssignment(supplier, row.plantCode, row.service);
  return (pa?.coverageStatus || '') === 'On Call';
}

/**
 * Days on an On Call row that carry hours (future days skipped), keyed
 * by DAY_KEYS: { actual, dir: 'oncall' }. Null when none / not On Call.
 */
export function onCallDays(row, suppliers, dates) {
  if (!rowIsOnCall(row, suppliers)) return null;
  const out = {};
  for (let i = 0; i < DAY_KEYS.length; i += 1) {
    const k = DAY_KEYS[i];
    const date = (dates || [])[i];
    if (!date || isFutureDate(date)) continue;
    const actual = Number(row.values?.[k]) || 0;
    if (actual > 0) out[k] = { actual, dir: 'oncall' };
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * The days on a row that need their own Explanation: every worked day
 * for an On Call supplier, otherwise the days off standard.
 */
export function explanationDays(row, plants, suppliers, dates) {
  if (rowIsOnCall(row, suppliers)) return onCallDays(row, suppliers, dates);
  return standardHoursDeviations(row, plants, suppliers, dates);
}

/**
 * Unified "does this row deviate from standard" test used by the
 * auto-expand-Explanation effect and submit validation. Weekly-billing
 * Standard rows use the weekly-total rule; everyone else uses the
 * per-day rule.
 */
function rowDeviates(row, plants, suppliers, dates) {
  // On Call rows open the Explanation panel once any day has hours.
  if (rowIsOnCall(row, suppliers)) return !!onCallDays(row, suppliers, dates);
  if (
    (row?.coverageType || 'Standard') === 'Standard' &&
    supplierPlantBillsWeekly(row.plantCode, row.supplierCode, suppliers, row.service, dates)
  ) {
    return !!weeklyStandardDeviation(row, plants, suppliers, dates);
  }
  return !!standardHoursDeviations(row, plants, suppliers, dates);
}

/**
 * Required-field validation at submit time. Returns a list of
 * human-readable messages for each missing required field, scoped by
 * entry number so the submitter can tell which row has the gap.
 *
 * Rules:
 *   - Plant and Supplier are always required on a row that has any
 *     other data in it (a completely blank row is just a template).
 *   - A row also needs SOMETHING entered — at least one day value > 0,
 *     or (for Expenses) an amount > 0.
 *   - Explanation is required when Coverage Type is anything other
 *     than Standard.
 *   - Secondary Facility is never required.
 *   - Receipts: required for every expense with an amount. See
 *     `expenseRowRequiresReceipt` for the call.
 *
 * `suppliers` is the loaded suppliers list — used to look up each row's
 * supplier so a message can name it. Pass an empty array if the caller
 * doesn't have suppliers loaded.
 */
function validateRowsForSubmit(rows, suppliers = [], plants = [], dates = []) {
  const errors = [];
  // Build a quick lookup once so the per-row loop below doesn't pay
  // an O(n×m) find on every Expenses row.
  const supplierByCode = new Map();
  for (const s of suppliers || []) {
    if (s?.SupplierCode) {
      supplierByCode.set(String(s.SupplierCode).toUpperCase(), s);
    }
  }
  const plantTitleByCode = new Map();
  for (const p of plants || []) {
    const code = String(p?.PlantCode || p?.plantCode || '').toUpperCase();
    if (code) plantTitleByCode.set(code, p.PlantName || p.Title || p.Name || code);
  }
  // A row "has data" only when the submitter actually entered something —
  // hours, an amount, an explanation, a Paid-To, or a mileage day. A row
  // that carries ONLY a Plant + Supplier is an auto-seeded template the
  // submitter never touched (the tracker pre-seeds every assigned plant ×
  // supplier), so it must NOT be treated as an in-progress entry —
  // otherwise every un-worked supplier trips "enter at least one day" and
  // blocks the whole submit. Seed-only rows are simply skipped.
  const anyRowHasAnyData = rowHasData;
  rows.forEach((row, i) => {
    // Completely blank rows AND untouched seeded rows are fine — they're
    // just unfilled templates and don't get submitted.
    if (!anyRowHasAnyData(row)) return;
    // Tag the entry with its Plant / Supplier so the submitter can find
    // the row a message refers to — "Entry 23" alone isn't locatable.
    const plantLabel = row.plantCode
      ? plantTitleByCode.get(String(row.plantCode).toUpperCase()) ||
        row.plantCode
      : '';
    const supplierName = row.supplierCode
      ? supplierByCode.get(String(row.supplierCode).toUpperCase())?.Title ||
        row.supplierCode
      : '';
    const idLabel = [plantLabel, row.supplierCode].filter(Boolean).join(' · ');
    const tag = idLabel ? `Entry ${i + 1} (${idLabel})` : `Entry ${i + 1}`;
    // Plain-language "who / where" for the missing-value messages, e.g.
    // "American Expedition Vehicles at GM Flint Assembly".
    const where =
      supplierName && plantLabel
        ? `${supplierName} at ${plantLabel}`
        : supplierName || plantLabel || `entry ${i + 1}`;
    if (!row.plantCode) errors.push(`${tag}: select a Plant`);
    if (!row.supplierCode) errors.push(`${tag}: select a Supplier`);
    if (
      EXPLANATION_REQUIRED_COVERAGE_TYPES.has(row.coverageType) &&
      !(row.explanation || '').trim()
    ) {
      errors.push(`${tag}: add an Explanation for ${row.coverageType}`);
    }
    // Standard hours that differ from the auto-filled standard — under
    // OR over — need an Explanation too. The blue/red day cells flag
    // the same days in the grid (per Jillian, July 2026).
    {
      const onCall = rowIsOnCall(row, suppliers);
      const isWeekly =
        !onCall &&
        (row.coverageType || 'Standard') === 'Standard' &&
        supplierPlantBillsWeekly(row.plantCode, row.supplierCode, suppliers, row.service, dates);
      if (isWeekly) {
        // Weekly-billing suppliers are judged on the week's TOTAL, not
        // each day, so a single row-level Explanation is required only
        // when the total is off standard.
        const weeklyDev = weeklyStandardDeviation(
          row,
          plants,
          suppliers,
          dates,
        );
        if (weeklyDev && !(row.explanation || '').trim()) {
          errors.push(
            `${tag}: add an Explanation — weekly total ${weeklyDev.actual} hrs differs from standard ${weeklyDev.expected} hrs`,
          );
        }
      } else if ((row.coverageType || 'Standard') === 'Standard') {
        const devs = explanationDays(row, plants, suppliers, dates);
        if (devs) {
          // Per-day rule (Sep 2026): every flagged day needs its own note.
          // A legacy row-level note (draft saved before per-day notes)
          // counts for the first flagged day only.
          const per = row.dayExplanations || {};
          const anyPer = Object.values(per).some((t) => String(t || '').trim());
          const legacy = String(row.explanation || '').trim();
          const missing = [];
          Object.entries(devs).forEach(([k, d], i) => {
            const has =
              String(per[k] || '').trim() || (!anyPer && legacy && i === 0);
            if (!has) {
              const dayIdx = DAY_KEYS.indexOf(k);
              missing.push(
                onCall
                  ? `${dayLabel((dates || [])[dayIdx], k)} (${d.actual} hrs)`
                  : `${dayLabel((dates || [])[dayIdx], k)} (${d.actual} vs standard ${d.expected})`,
              );
            }
          });
          if (missing.length) {
            errors.push(
              onCall
                ? `${tag}: On Call — add an Explanation for each day worked — missing ${missing.join(', ')}`
                : `${tag}: add an Explanation for each day off standard — missing ${missing.join(', ')}`,
            );
          }
        }
      }
    }
    // Expense and Mileage rows must name who to pay — the Paid To
    // dropdown lists the team members assigned to the row's plant
    // (per Jillian, July 2026).
    if (
      (row.coverageType === 'Expenses' || row.coverageType === 'Mileage') &&
      !(row.assignedTo || '').trim()
    ) {
      errors.push(`${tag}: select who to pay (Paid To)`);
    }
    // Mileage records a single day (full date) + miles amount; require
    // the Day whenever the row has miles on it (per Jillian, Aug 2026).
    if (row.coverageType === 'Mileage') {
      const populated = DAY_KEYS.filter(
        (k) => (Number(row.values?.[k]) || 0) > 0,
      );
      // Known when an explicit date is set, a legacy day key is set, or
      // the single populated weekday cell makes it inferable.
      const dayKnown =
        !!row.mileageDate || !!row.mileageDay || populated.length === 1;
      if (populated.length > 0 && !dayKnown) {
        errors.push(`${tag}: select a Day for the mileage`);
      }
    }
    const hasValue =
      row.coverageType === 'Expenses'
        ? (Number(row.expenseAmount) || 0) > 0
        : DAY_KEYS.some((k) => (Number(row.values[k]) || 0) > 0);
    if (!hasValue) {
      if (row.coverageType === 'Expenses') {
        errors.push(
          `You're missing an expense amount for ${where} — either enter the amount, or hit the red X on that line to remove it from your Billing Tracker.`,
        );
      } else if (row.coverageType === 'Mileage') {
        errors.push(
          `You're missing miles for ${where} — either enter the miles, or hit the red X on that line to remove it from your Billing Tracker.`,
        );
      } else {
        errors.push(
          `You're missing hours for ${where} — either add hours, or hit the red X on that line to remove that supplier from your Billing Tracker.`,
        );
      }
    }
    // Receipt rule — a receipt is required for every expense.
    if (row.coverageType === 'Expenses') {
      const supplier = row.supplierCode
        ? supplierByCode.get(String(row.supplierCode).toUpperCase())
        : null;
      if (
        expenseRowRequiresReceipt(row, supplier) &&
        !(row.receiptFileName || '').trim()
      ) {
        errors.push(
          `You need to upload a receipt for the expense on ${where} — every expense requires a receipt.`,
        );
      }
    }
  });
  // Overage-approval hard block — any (plant, supplier) that requires
  // written approval AND went over standard this week must have an
  // approval file attached before the tracker can be submitted.
  const supplierNameByCode = new Map(
    (suppliers || []).map((s) => [
      String(s.SupplierCode || '').trim().toUpperCase(),
      s.Title || s.SupplierCode,
    ]),
  );
  const plantTitleByCode2 = new Map();
  for (const p of plants || []) {
    const code = String(p?.PlantCode || p?.plantCode || '').toUpperCase();
    if (code) plantTitleByCode2.set(code, p.PlantName || p.Title || p.Name || code);
  }
  for (const need of overageApprovalNeeds(rows, suppliers, plants, dates)) {
    if (!need.hasOverage || need.hasFile) continue;
    const supplierName =
      supplierNameByCode.get(String(need.supplierCode).toUpperCase()) ||
      need.supplierCode;
    const plantName =
      plantTitleByCode2.get(String(need.plantCode).toUpperCase()) ||
      need.plantCode;
    errors.push(
      `You need to upload written overage approval for ${supplierName} at ${plantName} — this supplier requires it whenever hours go over standard.`,
    );
  }
  return errors;
}

// True when the supplier's coverage at this row's plant (and service) has
// an End Date that falls before `date` (Sep 2026). Such days autofill to 0,
// get the gray wash + "Coverage ended" tooltip, and any hours typed there
// count as over-standard so an Explanation is required.
function rowCoverageEndedOn(row, suppliers, date) {
  if (!row?.plantCode || !row?.supplierCode || !date) return false;
  const supplier = (suppliers || []).find(
    (s) => (s.SupplierCode || '') === row.supplierCode,
  );
  if (!supplier) return false;
  const pa = findAssignment(supplier, row.plantCode, row.service);
  return Boolean(pa) && coverageEndedOn(pa, date);
}

// True if any of the plant's PlantClosures entries covers `date`. Closures
// are [{ startDate, endDate }] where both are YYYY-MM-DD strings.
function isPlantClosedOn(plant, date) {
  if (!plant || !date) return false;
  const iso = formatLocalDateIso(date);
  return (plant.plantClosures || []).some((c) => {
    const s = (c.startDate || '').slice(0, 10);
    const e = (c.endDate || c.startDate || '').slice(0, 10);
    if (!s) return false;
    return iso >= s && iso <= (e || s);
  });
}

function formatLocalDateIso(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

// True if `date` falls strictly AFTER today's local-date. Used to disable
// timesheet day cells whose date hasn't happened yet — a timesheet is a
// log of completed work, not a forecast, so future cells are read-only.
// Today itself is allowed (you might be logging hours mid-day).
function isFutureDate(date) {
  if (!date) return false;
  const todayIso = formatLocalDateIso(new Date());
  const thatIso = formatLocalDateIso(date);
  return thatIso > todayIso;
}

// Submission opens on the Saturday that ENDS the billing week (the week
// runs Sun→Sat, so dates[6] is that Saturday). Before that Saturday the
// tracker can be saved as a draft but not submitted — a week still in
// progress can't be submitted early by accident. On the Saturday itself
// and afterward, submission is open. Past weeks are always open. Returns
// { open, opensLabel } where opensLabel is a friendly "Saturday, Aug 22,
// 2026" for the blocked-state message.
function weekSubmitWindow(dates) {
  if (!dates || dates.length < 7) return { open: true, opensLabel: '' };
  const saturday = dates[6];
  const satIso = formatLocalDateIso(saturday);
  const todayIso = formatLocalDateIso(new Date());
  return {
    open: todayIso >= satIso,
    opensLabel: saturday.toLocaleDateString(undefined, {
      weekday: 'long',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }),
  };
}

// Pretty approval stamp — "Apr 23, 2026 at 8:14 PM". Falls back to the
// raw ISO string when the timestamp can't be parsed so a bad row
// doesn't render as blank. Used on the Approved-state footer.
function formatApprovalDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// Submission deadline — the Monday AFTER the week in view. Reads as
// "Monday 4/27" so the reader can see both the weekday and the actual
// date without having to count forward from today. Rendered in bold
// orange by the caller to make it stand out in the subtitle.
function formatSubmissionDeadline(dates) {
  if (!dates || dates.length < 7) return '';
  // The week runs Sunday → Saturday, so dates[6] is the Saturday that
  // ends the week; the Monday after it is two days later.
  const saturday = dates[6];
  const nextMonday = new Date(
    saturday.getFullYear(),
    saturday.getMonth(),
    saturday.getDate() + 2,
  );
  const weekday = nextMonday.toLocaleDateString(undefined, {
    weekday: 'long',
  });
  const md = `${nextMonday.getMonth() + 1}/${nextMonday.getDate()}`;
  return `${weekday} ${md}`;
}

// "Apr 20 – Apr 26, 2026" style range for the card subtitle.
function formatWeekRange(dates) {
  if (!dates || dates.length < 7) return '';
  const first = dates[0];
  const last = dates[6];
  const sameMonth = first.getMonth() === last.getMonth();
  // 'long' spells the month out in full ("April" instead of "Apr").
  // When the range straddles two months, both ends carry their full
  // month name (e.g., "April 27 – May 3, 2026"); same-month ranges
  // only show the month once on the leading date.
  const fmt = (d, withMonth) =>
    d.toLocaleDateString(undefined, {
      month: withMonth ? 'long' : undefined,
      day: 'numeric',
    });
  return `${fmt(first, true)} – ${fmt(last, !sameMonth)}, ${last.getFullYear()}`;
}

function StatusBadge({ status }) {
  const palette = {
    [STATUS.DRAFT]: { bg: 'rgba(100, 116, 139, 0.15)', fg: '#475569' },
    [STATUS.PENDING]: { bg: 'rgba(202, 138, 4, 0.12)', fg: '#ca8a04' },
    [STATUS.APPROVED]: { bg: 'rgba(22, 163, 74, 0.12)', fg: 'var(--brand-success, #16a34a)' },
    [STATUS.REJECTED]: { bg: 'rgba(220, 38, 38, 0.12)', fg: 'var(--brand-danger)' },
  };
  const colors = palette[status] || palette[STATUS.DRAFT];
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: 999,
        background: colors.bg,
        color: colors.fg,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      }}
    >
      {status}
    </span>
  );
}

/**
 * Standard-coverage auto-fill — day-of-week aware. Looks up the
 * supplier's Plant Locations & Coverage row for the given plant, reads
 * the plant's weekday / Saturday / Sunday shift counts, and produces
 * the expected hours for each of the seven day cells. Returns an
 * object keyed by DAY_KEYS so the caller can drop values straight
 * into a row's `values`.
 *
 * Math by hoursUnit:
 *   • Per Shift  → hours-per-shift × shifts on each day
 *                  (Mon-Fri × Weekday count, Sat × Saturday count,
 *                  Sun × Sunday count)
 *   • Per Day    → flat allocation on every day the plant runs
 *                  (days with 0 shifts get 0)
 *   • Per Week   → distributed across shifts the supplier covers,
 *                  weighted by shifts per day so a 3-shift weekday
 *                  gets proportionally more than a 1-shift Saturday
 *   • Per Month  → same weighted distribution × ~4 weeks
 *
 * Weekend days only auto-fill when the PLANT runs shifts that day
 * (plant.ShiftsSaturdays / ShiftsSundays > 0) — the plant's schedule is
 * the single source of truth for which days coverage happens.
 *
 * Returns null when there's no usable allocation to convert — the
 * caller leaves the row's cells blank so the liaison can type their
 * own values.
 */
function computeStandardHoursByDay(plantCode, supplierCode, plants, suppliers, service, dates) {
  const supplier = (suppliers || []).find(
    (s) => (s.SupplierCode || '') === supplierCode,
  );
  if (!supplier) return null;
  // `service` picks between several rows at the same plant (Sep 2026) so
  // a Lineside Support row auto-fills from ITS coverage hours, not the
  // standard row's.
  const pa = findAssignment(supplier, plantCode, service);
  if (!pa) return null;
  // Guard: only a plain list of plants is usable here; anything else
  // (e.g. a Map passed by mistake) means "no plant", not a crash.
  const plant = (Array.isArray(plants) ? plants : []).find(
    (p) => (p.PlantCode || '') === plantCode,
  );
  if (!plant) return null;

  // Coverage can change over time (Oct 2026). Without the week's dates,
  // use today's coverage. With them, each day uses the coverage in effect
  // that day, except weekly-billing suppliers: their whole week is one
  // lump on Monday, so Monday's coverage decides the week.
  if (!Array.isArray(dates) || dates.length !== DAY_KEYS.length) {
    const todayCov = coverageOn(pa, new Date());
    const res = standardHoursForCoverage(todayCov, plant, dates);
    if (!res) return res;
    const adjusted = { ...res };
    for (const k of ['sat', 'sun']) {
      if (!weekendDayBilled(pa, k, todayCov)) adjusted[k] = 0;
    }
    return adjusted;
  }
  const monCov = coverageOn(pa, dates[DAY_KEYS.indexOf('mon')]);
  const isWeekly = (cov) => String(cov?.hoursUnit || '').trim().toLowerCase() === 'per week';
  if (isWeekly(monCov)) return standardHoursForCoverage(monCov, plant, dates);
  const out = { sun: 0, mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0 };
  const cache = new Map();
  let any = false;
  for (let i = 0; i < DAY_KEYS.length; i += 1) {
    const cov = coverageOn(pa, dates[i]);
    // A switch TO weekly billing mid-week starts with next week's Monday.
    if (!cov || isWeekly(cov)) continue;
    const key = `${cov.hoursAllocated}|${cov.hoursUnit}`;
    if (!cache.has(key)) cache.set(key, standardHoursForCoverage(cov, plant, dates));
    const byDay = cache.get(key);
    if (!byDay) continue;
    // Weekend billing (Nov 2026): a supplier that doesn't pay for
    // Saturday / Sunday work at this plant gets 0 on that day.
    out[DAY_KEYS[i]] = weekendDayBilled(pa, DAY_KEYS[i], cov)
      ? byDay[DAY_KEYS[i]] || 0
      : 0;
    any = true;
  }
  return any ? out : null;
}

/**
 * Does this supplier bill for work on `dayKey` at this plant? Weekdays
 * always do. Saturday / Sunday follow the plant row's billSaturdays /
 * billSundays checkboxes (Nov 2026). Only an explicit tick means billed;
 * older rows with no value count as not billed (per Jillian). Weekly and
 * monthly coverage ignore the flags (they bill one lump on Monday / the
 * 15th, per Jillian).
 */
function weekendDayBilled(pa, dayKey, cov) {
  if (dayKey !== 'sat' && dayKey !== 'sun') return true;
  const unit = String(cov?.hoursUnit || '').trim().toLowerCase();
  if (unit === 'per week' || unit === 'per month') return true;
  return dayKey === 'sat' ? pa?.billSaturdays === true : pa?.billSundays === true;
}

/**
 * Standard hours per day for ONE coverage amount ({ hoursAllocated,
 * hoursUnit }) at a plant, using the plant's shift counts. `dates` only
 * matters for weekly billing (closure on Monday moves the lump).
 */
function standardHoursForCoverage(cov, plant, dates) {
  if (!cov || !plant) return null;
  const h = Number(cov?.hoursAllocated);
  if (!Number.isFinite(h) || h <= 0) return null;

  // Plant shift counts. Legacy ShiftsPerDay falls back into the
  // weekday slot (with 0 for weekend days) so a plant that hasn't
  // been re-saved into the new fields still produces a sensible
  // auto-fill.
  const numOr = (v, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : fallback;
  };
  const legacy = Math.max(0, Number(plant.ShiftsPerDay) || 0);
  const weekdayShifts = numOr(plant.ShiftsWeekdays, legacy);
  const saturdayShifts = numOr(plant.ShiftsSaturdays, 0);
  const sundayShifts = numOr(plant.ShiftsSundays, 0);

  // Whether the plant runs weekend shifts — the plant's schedule is the
  // single source of truth for which days coverage happens.
  const covSat = saturdayShifts > 0;
  const covSun = sundayShifts > 0;

  const unit = (cov?.hoursUnit || '').toLowerCase();
  let wdHrs = 0;
  let saHrs = 0;
  let suHrs = 0;

  if (unit === 'per shift') {
    wdHrs = h * weekdayShifts;
    saHrs = h * saturdayShifts;
    suHrs = h * sundayShifts;
  } else if (unit === 'per day') {
    wdHrs = weekdayShifts > 0 ? h : 0;
    saHrs = saturdayShifts > 0 ? h : 0;
    suHrs = sundayShifts > 0 ? h : 0;
  } else if (unit === 'per week') {
    // Weekly-billing suppliers bill the WHOLE week's hours on Monday
    // instead of spreading them across production days (per Jillian,
    // Oct 2026): 10 hrs / week → 10 on Monday, 0 the rest of the week.
    // If Monday is a Plant Closure day (and the week's dates are known),
    // the hours move to the next day the plant is open so the week isn't
    // lost to the closure-days-stay-at-0 rule.
    const out = { sun: 0, mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0 };
    let target = 'mon';
    if (Array.isArray(dates) && dates.length === DAY_KEYS.length) {
      for (let i = DAY_KEYS.indexOf('mon'); i < DAY_KEYS.length; i += 1) {
        if (!dates[i] || !isPlantClosedOn(plant, dates[i])) {
          target = DAY_KEYS[i];
          break;
        }
      }
    }
    out[target] = Math.round(h * 100) / 100;
    return out;
  } else if (unit === 'per month' && Array.isArray(dates) && dates.length === DAY_KEYS.length) {
    // Monthly-billing suppliers bill the WHOLE month's hours on the 15th
    // (per Jillian, Oct 2026): 20 hrs / month → 20 on the 15th, 0 on every
    // other day. Weeks that don't contain a 15th get 0 all week. If the
    // 15th is a Plant Closure day, the hours move to the next day that
    // week the plant is open, so the month isn't lost to the closure rule.
    const out = { sun: 0, mon: 0, tue: 0, wed: 0, thu: 0, fri: 0, sat: 0 };
    const idx15 = dates.findIndex((d) => d instanceof Date && d.getDate() === 15);
    if (idx15 >= 0) {
      let target = idx15;
      // Days the plant doesn't run (closures, or a weekend with no
      // shifts) are skipped when moving forward; the 15th itself always
      // counts unless it's a closure.
      const runsOn = (d) => {
        const wd = d.getDay();
        if (wd === 6) return saturdayShifts > 0;
        if (wd === 0) return sundayShifts > 0;
        return true;
      };
      for (let i = idx15; i < DAY_KEYS.length; i += 1) {
        if (!isPlantClosedOn(plant, dates[i]) && (i === idx15 || runsOn(dates[i]))) {
          target = i;
          break;
        }
      }
      out[DAY_KEYS[target]] = Math.round(h * 100) / 100;
    }
    return out;
  } else if (unit === 'per month') {
    // No week dates (summary views): fall back to the even spread.
    const wd = weekdayShifts;
    const sa = covSat ? saturdayShifts : 0;
    const su = covSun ? sundayShifts : 0;
    const totalMonthlyShifts = (wd * 5 + sa + su) * 4;
    if (totalMonthlyShifts > 0) {
      const perShift = h / totalMonthlyShifts;
      wdHrs = perShift * wd;
      saHrs = perShift * sa;
      suHrs = perShift * su;
    } else {
      wdHrs = h / 20;
    }
  } else {
    return null;
  }

  // Force days outside the supplier's coverage to zero — even if the
  // math above produced a value, the supplier doesn't work that day.
  if (!covSat) saHrs = 0;
  if (!covSun) suHrs = 0;

  return roundDaysToTotal({
    mon: wdHrs,
    tue: wdHrs,
    wed: wdHrs,
    thu: wdHrs,
    fri: wdHrs,
    sat: saHrs,
    sun: suHrs,
  });
}

/**
 * Round each day's hours to 2 decimals WITHOUT losing the weekly total
 * (per Jillian, Oct 2026). Rounding 8 hrs/week spread over 12 shifts
 * day by day gave 1.33 ×5 + 0.67 ×2 = 7.99; the missing 0.01 belongs
 * somewhere. This rounds every day, then hands the leftover cents, one
 * at a time, to the covered day whose rounding lost the most (ties go to
 * the latest day in the week, so the fix lands on Friday rather than
 * Sunday). The result always sums to the 2-decimal weekly total.
 */
function roundDaysToTotal(raw) {
  const cents = {};
  let exact = 0;
  let sum = 0;
  for (const k of DAY_KEYS) {
    const v = Number(raw[k]) || 0;
    exact += v;
    cents[k] = Math.round(v * 100);
    sum += cents[k];
  }
  let diff = Math.round(exact * 100) - sum;
  let guard = 0;
  while (diff !== 0 && guard < 14) {
    guard += 1;
    const step = diff > 0 ? 1 : -1;
    let best = null;
    let bestErr = -Infinity;
    for (const k of DAY_KEYS) {
      const v = Number(raw[k]) || 0;
      if (v <= 0) continue; // never put hours on a day with no coverage
      const err = step * (v * 100 - cents[k]);
      if (err >= bestErr) {
        bestErr = err;
        best = k;
      }
    }
    if (!best) break;
    cents[best] += step;
    diff -= step;
  }
  const out = {};
  for (const k of DAY_KEYS) out[k] = cents[k] / 100;
  return out;
}

/**
 * Convert a hoursAllocated + hoursUnit pair (as stored on a supplier's
 * plant-assignment row) into a daily-hours number. Mirrors the same
 * helper on SupplierDetail.jsx — duplicated rather than extracted so
 * both pages stay self-contained; keep the two in sync if the unit set
 * ever grows.
 */
function computeHoursPerDay(hoursAllocated, hoursUnit, shiftsPerDay) {
  const h = Number(hoursAllocated);
  if (!Number.isFinite(h)) return null;
  const unit = (hoursUnit || '').toLowerCase();
  if (unit === 'per day') return h;
  if (unit === 'per shift') {
    const s = Number(shiftsPerDay);
    if (!Number.isFinite(s) || s <= 0) return null;
    return h * s;
  }
  if (unit === 'per week') return h / 5;
  if (unit === 'per month') return h / 20;
  return null;
}

// Stashed here for use by a later RM approval inbox task. Re-exported so
// the inbox page can share this page's helpers without duplicating them.
export const __rmHelpers = { approveTimesheet, rejectTimesheet };

/**
 * Expenses row body — replaces the seven-day grid that other coverage
 * types use. Three logical zones from left to right:
 *
 *   1. Receipt — Upload Receipt button before any file is attached;
 *      filename link (target=_blank) + Replace / Remove controls after
 *      a file has been uploaded. Disabled until Plant + Supplier are
 *      filled so the canonical filename has all of its segments.
 *   2. Amount ($) — narrow numeric input. Sized smaller than the old
 *      full-width version so the receipt UI gets room to breathe.
 *   3. Total — right-aligned mirror of the amount, kept so the
 *      Expenses row terminates with the same Total column the Hours /
 *      Mileage rows show on the right edge.
 */
function ExpenseRow({
  row,
  canEdit,
  rowReady,
  saving,
  receiptState,
  onAmountChange,
  onUploadReceipt,
  onClearReceipt,
  formatRowTotal,
}) {
  const fileInputRef = useRef(null);
  const uploading = receiptState === 'uploading';
  const errorMsg =
    typeof receiptState === 'string' && receiptState !== 'uploading'
      ? receiptState
      : '';
  const hasReceipt = !!row.receiptFileName;
  const inputDisabled = !canEdit || !rowReady || saving || uploading;

  const triggerPicker = () => {
    if (inputDisabled) return;
    fileInputRef.current?.click();
  };

  const onFileChange = (e) => {
    const file = e.target.files?.[0];
    // Reset the native input so re-picking the same file still fires
    // change. No-op when the user cancels (file is undefined).
    e.target.value = '';
    if (file) onUploadReceipt(file);
  };

  return (
    <div
      style={{
        display: 'grid',
        // Receipt zone takes the bulk of the row; Amount is the narrow
        // input Jillian asked for; Total is the right-aligned mirror.
        gridTemplateColumns: '1fr 140px minmax(90px, auto)',
        gap: 14,
        marginTop: 14,
        alignItems: 'end',
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--brand-ink)',
            marginBottom: 4,
          }}
        >
          Receipt *
        </div>
        {hasReceipt ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              padding: '6px 0',
            }}
          >
            {/* Filename as a clickable link — target=_blank opens the
                SharePoint preview in a new browser tab so the
                timesheet stays put. The href falls back to "#" if for
                some reason a row has a name without a URL (shouldn't
                happen, but never blow up on render). */}
            <a
              href={row.receiptUrl || '#'}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: 13,
                color: 'var(--brand-primary, #E87722)',
                wordBreak: 'break-all',
              }}
              title="Open receipt in a new window"
            >
              {row.receiptFileName}
            </a>
            {canEdit && !saving && (
              <>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={triggerPicker}
                  disabled={inputDisabled}
                  title="Replace this receipt with a different file"
                >
                  Replace
                </button>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={onClearReceipt}
                  disabled={saving}
                  title="Detach this receipt from the row (file stays in SharePoint)"
                >
                  Remove
                </button>
              </>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              className="btn btn-outline btn-sm"
              onClick={triggerPicker}
              disabled={inputDisabled}
              title={
                !rowReady
                  ? 'Pick a Plant and Supplier first'
                  : 'Upload Receipt'
              }
            >
              {uploading ? 'Uploading…' : 'Upload Receipt'}
            </button>
          </div>
        )}
        {errorMsg && (
          <div
            style={{
              fontSize: 12,
              color: 'var(--brand-danger)',
              marginTop: 6,
            }}
          >
            {errorMsg}
          </div>
        )}
        {/* Hidden native file input — clicked programmatically by the
            visible buttons above. accept restricts the picker to the
            common receipt formats; SharePoint accepts the lot. */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,application/pdf"
          style={{ display: 'none' }}
          onChange={onFileChange}
        />
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--brand-ink)',
            marginBottom: 4,
          }}
        >
          Amount ($)
        </div>
        <TsNumberInput
          step={0.01}
          min={0}
          // Empty string when the amount is 0 so the "$0" placeholder
          // shows; consistent with the hours / miles cells above.
          value={row.expenseAmount || ''}
          onChange={onAmountChange}
          disabled={!canEdit || !rowReady || saving}
          placeholder="$0"
          title={
            !rowReady ? 'Pick a Plant and Supplier first' : undefined
          }
        />
      </div>
      <div>
        <div
          style={{
            textAlign: 'right',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--brand-muted, #64748b)',
            textTransform: 'uppercase',
            letterSpacing: 0.4,
            marginBottom: 4,
          }}
        >
          Total
        </div>
        <div
          style={{
            textAlign: 'right',
            fontWeight: 700,
            fontSize: 16,
            padding: '6px 4px',
          }}
        >
          {formatRowTotal(row)}
        </div>
      </div>
    </div>
  );
}
